import { EventChangeArg, EventClickArg, EventContentArg, EventHoveringArg } from '@fullcalendar/core';
import { EventImpl } from '@fullcalendar/core/internal';
import interactionPlugin, { Draggable, DropArg } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { ResourceLabelContentArg } from '@fullcalendar/resource';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import {
  Backdrop,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  lighten,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Theme,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { addMinutes, addSeconds } from 'date-fns';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, FC, memo, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import DirectionSettingDialog from 'src/components/DirectionSettingDialog';
import { DRIVER_UNSET_LABEL, TRUCK_UNSET_LABEL } from 'src/constants/assignmentLabels';
import { actionBarHeight, appBarHeight } from 'src/constants/layout';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import LicenseContext from 'src/contexts/LicenseContext';
import { OrderSelectionContext } from 'src/contexts/OrderSelectionContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { AssignTruckEntity } from 'src/entities/AssignTruck.entity';
import { CalendarResourceEntity, CalendarResourceEntityWithChecked } from 'src/entities/CalendarResource.entity';
import { DeliveryEventEntity } from 'src/entities/DeliveryEvent.entity';
import { GarageEntity } from 'src/entities/Garage.entity';
import { LegRunOperationEventEntity, OperationEventEntity } from 'src/entities/OperationEvent.entity';
import { RespectType, Senario, WeekDayEn } from 'src/entities/planningEntity';
import { PlanningOrderStatisticsEntity } from 'src/entities/PlanningOrderStatistics.entity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { DetailItem, PlanningsOperationEntity } from 'src/entities/PlanningsOperation.entity';
import {
  PlanningsOperationEntitiesWithStatsByDeliveryIdEntity
} from 'src/entities/PlanningsOperationEntitiesWithStatsByDeliveryId.entity';
import { PlanningsOperationEntityWithStatsEntity } from 'src/entities/PlanningsOperationEntityWithStats.entity';
import { PlanningSuggestion } from 'src/entities/PlanningsSuggestions.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { PositionEntity } from 'src/entities/PositionEntity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { useNavigationGuard } from 'src/hooks/useNavigationGuard';
import { Operations } from 'src/models/Operations';
import { OperationsWithStatusModel } from 'src/models/OperationsWithStatus.model';
import arrayUtil from 'src/utils/array.util';
import { isOutsideBerthReservationTime } from 'src/utils/berthReservationTime.util';
import { debounce } from 'src/utils/debounce';
import numberUtil from 'src/utils/number.util';
import { calendarDurationDays } from 'src/utils/planningDateRange.util';
import { EstimationMethodVo } from 'src/vo/EstimationMethod.vo';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';
import { TruckDisplayStatusVo } from 'src/vo/TruckDisplayStatusVo';

import NotAllocSuggestionComponent from '../V3Plans/components/NotAllocSuggestion.component';
import ActionsPresenter from '../V3Plans/presenters/ActionsPresenter';
import AllocateHistoryPresenter from '../V3Plans/presenters/AllocateHistoryPresenter';
import EstimationDialogPresenter from '../V3Plans/presenters/EstimationDialogPresenter';
import OrdersPresenter from '../V3Plans/presenters/OrdersPresenter';
import PlanningOrderPresenter from '../V3Plans/presenters/PlanningOrder.presenter';
import PlanningPresenter from '../V3Plans/presenters/PlanningPresenter';
import PlanningTemporaryTruckDialog from '../V3Plans/presenters/PlanningTemporaryTruckDialog';
import { ScenarioPlanningListDialog } from '../V3Plans/presenters/ScenarioPlanningListDialog';
import SelectDriverMenuPresenter, { SelectDriverMenuDriverEntity } from '../V3Plans/presenters/SelectDriverMenu.presenter';
import SelectTruckMenuPresenter, { SelectTruckMenuTruckEntity } from '../V3Plans/presenters/SelectTruckMenu.presenter';
import TariffCalculationDialog, { TariffCalculationParams } from '../V3Plans/presenters/TariffCalculationDialog';
import TruckSelectMenuPresenter from '../V3Plans/presenters/TruckSelectMenuPresenter';

import { isCrossCompanyDrop, isCrossCompanyMove, resolveDeliveryCompany } from './companyGuard';
import CostAnalysisDialog from './presenters/CostAnalysisDialog';
import OperationInfoPanel from './presenters/OperationInfoPanel';
import ResourceStackMemo from './presenters/ResourceStackMemo';
import TruckInfoPanel from './presenters/TruckInfoPanel';
import { legRunOperationEventsReducer, operationEntitiesReducer } from './Reducer';
import { buildTimelineResourceId } from './resourceId';

type DriverDailyCost = {
  deliveryId: number;
  driverId: number;
  truckId: number;
  truckName: string;
  truckKlass: string;
  date: string; // YYYY-MM-DD形式
  workingHours: number; // 時間単位
  dayStartAt: Date;
  dayEndAt: Date;
  cost: number;
  sales: number;
};

type Props = {
  startDate: Date;
  endDate: Date;
  garageData: GarageEntity[];
  deliveryEvents: DeliveryEventEntity[];
  resources: CalendarResourceEntity[];
  saveTimeline: (operationEntities: PlanningsOperationEntity[], emptyDeliveryIds: number[], latestAlgorithmVersion: number) => void;
  truckData: PlanningsTruckEntity[];
  mutateDeleteOrdersOperations: (requestOrderIds: number[]) => void;
  isLoading: boolean;
  groupEntities: PlanningsGroupEntity[];
  mutateDeleteSpecificOrder: (orderId: number) => void;
  printOperationDirectionUrl: () => string;
  addSelectedOrderId: (id: number) => void;
  removeSelectedOrderId: (id: number) => void;
  unit: string;
  mutateDeleteOrder: () => void;
  mutateCloneOrder: (shipimentOrderId: number) => void;
  planningOrderStatisticsEntity: PlanningOrderStatisticsEntity | undefined;
  customInputFields: string[];
  driverEntities: PlanningsDriverEntity[];
  deliveryEntities: PlanningsDeliveryEntity[];
  shipmentOrderLegRunEntityMap: Map<number, ShipmentOrderLegRunForPlanEntity>;
  directionSettingDialogIsOpen: boolean;
  updateDirectionSettingDialogIsOpen: (bool: boolean) => void;
  mutateRestoreSplittedOrder: (orderId: number) => void;
  mutateRestoreSplittedOrders: () => void;
  truckDisplayStatus: TruckDisplayStatusVo;
  planningRequest: (shiftIds: number[], orderIds: number[]) => void;
  updateBalancedLoading: (balancedLoading: boolean) => void;
  updateIsRespectRecent: (bool: boolean) => void;
  updateExcludeToll: (bool: boolean) => void;
  updateRespectType: (respectType: RespectType) => void;
  updateRespectDaysOfWeek: (days: WeekDayEn[]) => void;
  updateRespectOn: (on: string) => void;
  updateIsTimeTableBackward: (bool: boolean) => void;
  updateIncludeFutureDeliveries: (bool: boolean) => void;
  updateAutoSplitOrders: (bool: boolean) => void;
  updateDontExchangePreroute: (bool: boolean) => void;
  updateConcurrentAllOrNothing: (bool: boolean) => void;
  relaxedRules: RelaxedRuleVo[];
  selectedRelaxedRules: RelaxedRuleVo[];
  setSelectedRelaxedRules: (value: RelaxedRuleVo[]) => void;
  setPriorityLargeTrucks: (priority: number) => void;
  priorityLargeTrucks: number;
  updateExpandWorkingStartMinutes: (minutes: number) => void;
  updateExpandWorkingEndMinutes: (minutes: number) => void;
  updateExpandLoadStartMinutes: (minutes: number) => void;
  updateExpandLoadEndMinutes: (minutes: number) => void;
  updateExpandUnloadStartMinutes: (minutes: number) => void;
  updateExpandUnloadEndMinutes: (minutes: number) => void;
  updateExpandMaxVolumeRate: (rate: number) => void;
  updateExpandMaxLoadCapacityRate: (rate: number) => void;
  updateMLSourceType: (mlSourceTypes: number) => void;
  updateMLPlanning: (bool: boolean) => void;
  updateOptimizeForLoad: (bool: boolean) => void;
  updateForceNullBase: (bool: boolean) => void;
  updateTrunkTransportation: (bool: boolean) => void;
  notAllocSuggestions: PlanningSuggestion[];
  rollbackRequest: () => void;
  currentHistoryVersion: string;
  setCurrentHistoryVersion: (version: string) => void;
  resetCurrentHistoryVersion: () => void;
  requestScenarioPlanning: (scenarioId: number) => void;
  assignTruckToTemporaryDeliveryRequest: (entity: AssignTruckEntity) => void;
  handleConfirmDeliveries: (deliveryIds: number[]) => void;
  handleUnconfirmDeliveries: (deliveryIds: number[]) => void;
  updateActivateIncrementalMaxNumPistons: (bool: boolean) => void;
  updateSenario: (senario: Senario) => void;
  updateDisableGlobalOpt: (bool: boolean) => void;
  updateTraceCompanyId: (id: number) => void;
  showLegRunLoading: boolean;
  handleTariffCalculationExecute: (params: TariffCalculationParams, onSuccess?: () => void) => void;
  tariffCalculationIsExecuting: boolean;
}

// 表示名: トラック未設定(null/temporary)は「車両未設定」、ドライバー未設定(null/temporary)は「ドライバー未設定」、
// それ以外は名称。
const truckDisplayName = (truck?: PlanningsTruckEntity | null): string => {
  if (!truck) return TRUCK_UNSET_LABEL;
  return truck.temporary ? TRUCK_UNSET_LABEL : truck.licensePlateValue;
};
const driverDisplayName = (driver?: PlanningsDriverEntity | null): string => {
  if (!driver) return DRIVER_UNSET_LABEL;
  return driver.temporary ? 'ドライバー未設定' : driver.name;
};

const Presenter: FC<Props> = memo(({
  startDate,
  endDate,
  garageData,
  deliveryEvents,
  resources,
  saveTimeline,
  truckData,
  mutateDeleteOrdersOperations,
  isLoading,
  groupEntities,
  mutateDeleteSpecificOrder,
  printOperationDirectionUrl,
  addSelectedOrderId,
  removeSelectedOrderId,
  unit,
  mutateDeleteOrder,
  mutateCloneOrder,
  planningOrderStatisticsEntity,
  customInputFields,
  driverEntities,
  deliveryEntities,
  shipmentOrderLegRunEntityMap,
  directionSettingDialogIsOpen,
  updateDirectionSettingDialogIsOpen,
  mutateRestoreSplittedOrder,
  mutateRestoreSplittedOrders,
  truckDisplayStatus,
  planningRequest,
  updateBalancedLoading,
  updateIsRespectRecent,
  updateExcludeToll,
  updateRespectType,
  updateRespectDaysOfWeek,
  updateRespectOn,
  updateIsTimeTableBackward,
  updateIncludeFutureDeliveries,
  updateAutoSplitOrders,
  updateDontExchangePreroute,
  updateConcurrentAllOrNothing,
  relaxedRules,
  selectedRelaxedRules,
  setSelectedRelaxedRules,
  setPriorityLargeTrucks,
  priorityLargeTrucks,
  updateExpandWorkingStartMinutes,
  updateExpandWorkingEndMinutes,
  updateExpandLoadStartMinutes,
  updateExpandLoadEndMinutes,
  updateExpandUnloadStartMinutes,
  updateExpandUnloadEndMinutes,
  updateExpandMaxVolumeRate,
  updateExpandMaxLoadCapacityRate,
  updateMLSourceType,
  updateMLPlanning,
  updateOptimizeForLoad,
  updateForceNullBase,
  updateTrunkTransportation,
  notAllocSuggestions,
  rollbackRequest,
  currentHistoryVersion,
  setCurrentHistoryVersion,
  resetCurrentHistoryVersion,
  requestScenarioPlanning,
  assignTruckToTemporaryDeliveryRequest,
  handleConfirmDeliveries,
  handleUnconfirmDeliveries,
  updateActivateIncrementalMaxNumPistons,
  updateSenario,
  updateDisableGlobalOpt,
  updateTraceCompanyId,
  showLegRunLoading,
  handleTariffCalculationExecute,
  tariffCalculationIsExecuting,
}) => {
  // 定数を追加
  const LOCAL_STORAGE_VIEW_KEY = 'timeline_view_preference';

  const theme: Theme = useTheme();
  const { enqueueSnackbar, closeSnackbar, } = useSnackbar();
  const navigate = useNavigate();
  const calendarRef = useRef(null);

  const context = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);
  const orderSelection = useContext(OrderSelectionContext);
  const { selectedGroupEntities, allocateHistories, calculating } = plansContext;
  const { orderIds, selectedOrderIds, allocatedOrderIds } = orderSelection;

  const unallocatedDrawerWidth = useMemo(() => 300, []);
  const [fixedShiftTruckIds, setFixedShiftTruckIds] = useState<number[]>([]);

  const loadColor = useMemo(() => '#039BE5', []);
  const loadColorLight = useMemo(() => lighten(loadColor, 0.25), [loadColor]);
  const unloadColor = useMemo(() => '#f4511E', []);
  const unloadColorLight = useMemo(() => lighten(unloadColor, 0.25), [unloadColor]);
  const loadUnloadMixColor = useMemo(() => '#2C72CF', []);
  const waittimeColor = useMemo(() => '#aaa', []);
  const waittimeColorLight = useMemo(() => lighten(waittimeColor, 0.25), [waittimeColor]);

  const [startOn, setStartOn] = useState<string>();
  const [endOn, setEndOn] = useState<string>();
  const [showNotAllocSuggestionSnackbar, setShowNotAllocSuggestionSnackbar] = useState(false);
  const [events, setEvents] = useState<(LegRunOperationEventEntity | DeliveryEventEntity)[]>([]);
  const [eventDialogSelectedOrderIds, setEventDialogSelectedOrderIds] = useState<number[]>([]);
  const [displayDetailOrderId, setDetailDisplayOrderId] = useState<number | undefined>(undefined);
  const [displayOrderDialogIsOpen, setDisplayOrderDialogIsOpen] = useState(false);
  const [openUnallocatedOrders, setOpenUnallocatedOrders] = useState<boolean>(true);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState<number>(0);
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState<boolean>(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState<string>('');
  const [selectedOperation, setSelectedOperation] = useState<LegRunOperationEventEntity | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // 変更フラグを管理するstate
  const [shouldSave, setShouldSave] = useState(false);
  const [popoverEl, setPopoverEl] = useState<HTMLElement | null>(null);
  const [mousePosition, setMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [popoverOperationEventEntity, setPopoverOperationEventEntity] = useState<EventImpl>(null);
  const [checkedResources, setCheckedResources] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<'none' | 'some' | 'every'>();
  const [selectedTruckInfo, setSelectedTruckInfo] = useState<PlanningsDeliveryEntity | null>(null);
  const [infoPopoverAnchorEl, setInfoPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [planningDialogIsOpen, setPlanningDialogIsOpen] = useState<boolean>(false);
  const [estimationDialogIsOpen, setEstimationDialogIsOpen] = useState<boolean>(false);
  const [estimationMethod, setEstimationMethod] = useState<EstimationMethodVo | undefined>(undefined);
  const [allocatedHistoriesDialogIsOpen, setAllocatedHistoriesDialogIsOpen] = useState<boolean>(false);
  const [allocateHistoriesForCompany, setAllocateHistoriesForCompany] = useState<AllocateHistoryEntity[]>([]);
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
  const [scenarioPlanningDialogIsOpen, setScenarioPlanningDialogIsOpen] = useState(false);
  const [tariffCalculationDialogIsOpen, setTariffCalculationDialogIsOpen] = useState(false);
  const [latestRequestAlgorithmVersion, setLatestRequestAlgorithmVersion] = useState<number | undefined>();
  const [modifiedOrderIds, setModifiedOrderIds] = useState<number[]>([]);
  // ローカルストレージから保存された値を取得、なければデフォルト値を返す
  const [initialView, setInitialView] = useState<string>(() => localStorage.getItem(LOCAL_STORAGE_VIEW_KEY) || 'resourceTimelineDayTwo');
  const [resetTooltip, setResetTooltip] = useState<string>('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [fixOrUnfix, setFixOrUnfix] = useState<'fix' | 'unfix'>('fix');
  const [resetTargetDeliveryIds, setResetTargetDeliveryIds] = useState<number[]>([]);
  const [temporaryTruckDialogOpen, setTemporaryTruckDialogOpen] = useState(false);
  const [truckAnchorEl, setTruckAnchorEl] = useState<null | HTMLElement>(null);
  const [truckEntity, setTruckEntity] = useState<PlanningsTruckEntity>(null);
  const [truckIdsHasDelivery, setTruckIdsHasDelivery] = useState<number[]>([]);
  const [driverAnchorEl, setDriverAnchorEl] = useState<null | HTMLElement>(null);
  const [driverEntity, setDriverEntity] = useState<PlanningsDriverEntity>(null);
  // ドライバー変更メニューを開いたリソースの deliveryId。ドライバー未設定シフト(driverId=null)は
  // 旧ドライバー id で対象 delivery を特定できないため、このシフト 1 件を対象にする。
  const [driverMenuDeliveryId, setDriverMenuDeliveryId] = useState<number | null>(null);
  const [driverIdsHasDelivery, setDriverIdsHasDelivery] = useState<number[]>([]);
  const [driverDailyCosts, setDriverDailyCosts] = useState<DriverDailyCost[]>([]);
  const [costAnalysisDialogOpen, setCostAnalysisDialogOpen] = useState(false);
  const [summaryElementHeight, setSummaryElementHeight] = useState<number>(0);
  const [summaryElementCount, setSummaryElementCount] = useState(3);
  const [fixedDeliveryIdsForUpdate, setFixedDeliveryIdsForUpdate] = useState<number[]>([]);

  // 離脱ガードは debouncedSave の定義後に置く（破棄時に保存タイマーを止めるため）。

  // 配送内の作業（operations）の変更を検出する関数
  const detectOperationChanges = useCallback((
    previousOps: PlanningsOperationEntity[],
    currentOps: PlanningsOperationEntity[]
  ): number[] => {
    // 作業数が異なる場合は変更あり
    if (previousOps.length !== currentOps.length) {
      const prevOrderIds = previousOps.map((it) => it.orderId);
      const currOrderIds = currentOps.map((it) => it.orderId);
      return currOrderIds.filter((it) => !prevOrderIds.includes(it));
    }

    // 作業の内容を比較
    return currentOps.filter((currentOp) => {
      const previousOp = previousOps.find((p) => p.orderId === currentOp.orderId
        && p.action === currentOp.action);

      if (!previousOp) {
        return true; // 新規作業
      }

      // 重要な属性の変更を検出
      return (
        currentOp.arrivalAt !== previousOp.arrivalAt
        || currentOp.departureAt !== previousOp.departureAt
        || currentOp.shiftTruckId !== previousOp.shiftTruckId
      );
    }).map((it) => it.orderId);
  }, []);

  // 配送の変更を検出する関数
  const detectDeliveryChanges = useCallback((previous: PlanningsDeliveryEntity[], current: PlanningsDeliveryEntity[]) => {
    // IDベースで配送を比較するためのマップを作成
    const previousMap = new Map(previous.map((delivery) => [delivery.id, delivery]));

    const changes = {
      added: [] as number[],
      modified: [] as number[]
    };

    // 新規追加と変更を検出
    current.forEach((delivery) => {
      const previousDelivery = previousMap.get(delivery.id);
      if (!previousDelivery) {
        // 新規追加
        changes.added.push(...delivery.operations.map((op) => op.orderId));
      } else {
        // 変更を検出（operationsの比較）
        const hasChanges = detectOperationChanges(previousDelivery.operations, delivery.operations);
        if (hasChanges) {
          changes.modified.push(...hasChanges);
        }
      }
    });

    return changes;
  }, [detectOperationChanges]);

  useEffect(() => {
    if (!allocateHistories) return;
    if (allocateHistories.length === 0) return;

    const changes = detectDeliveryChanges(allocateHistories[0].deliveryEntities, deliveryEntities);
    const ids = Array.from(new Set([
      ...changes.added,
      ...changes.modified,
    ]));
    setModifiedOrderIds(ids);
  }, [allocateHistories, deliveryEntities, detectDeliveryChanges]);

  useEffect(() => {
    setStartOn(datetimeDecorator.toYyyyMmDd(startDate));
    setEndOn(datetimeDecorator.toYyyyMmDd(endDate));
  }, [startDate, endDate]);

  // カスタムCSSをドキュメントに追加
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .custom-resource-label .fc-icon,
      .custom-resource-label .fc-datagrid-expander-placeholder {
        display: none !important;
      }

      .custom-resource-label .fc-datagrid-cell-main {
        display: flex;
        width: 100%;
        display: inline-block;
      }

      .fc-resource-timeline .fc-resource {
        min-height: 58px !important;
        display: flex;
        align-items: center;
        min-width: 100%;
      }

      .fc-resource-timeline .fc-timeline-body {
        overflow-x: scroll !important;
        scrollbar-gutter: stable;
      }

      .has-warning-event {
        border-color: red !importarnt;
        border-width: 3px !important;
      }
      .sales-table {
        border: 0px !important;
      }
      .sales-date {
        border: 0px !important;
        font-size: 0.8rem;
      }
      .sales-items {
        border: 0px !important;
        padding-left: 8px !important;
        font-size: 0.8rem;
      }
      .sales-details {
        border: 0px !important;
        font-size: 0.8rem;
        padding-left: 2px !important;
        text-align: right;
      }
      .sales-info {
        font-size: 0.9rem;
        float: right;
        margin-left: 20px;
      }
      .sales-good { color: #4caf50; padding-right: 8px; }
      .sales-bad { color: #f44336; padding-right: 8px; }
      .sales-normal { color: #666; padding-right: 8px; }

      .expand-sales-cost {
          margin-left: 20px;
      }

      .sales-cost-detail, .expand-sales-cost {
        color: #1976d2;
        cursor: pointer;
        text-decoration: underline;
        font-size: 0.8rem;
        margin-top: 4px;
        display: inline-block;
      }
      .sales-cost-detail:hover, .expand-sales-cost:hover {
        color: #1565c0;
        text-decoration: none;
      }
    `;
    if (document.head) {
      document.head.appendChild(style);
    }
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (!deliveryEntities) return;

    setFixedShiftTruckIds(deliveryEntities.filter((it) => it.fixed).map((it) => it.id));
  }, [deliveryEntities]);

  const buildOperationEntity: (
    orderEntity: ShipmentOrderLegRunForPlanEntity,
    operationId: number,
    startAt: Date,
    shiftTruckId: number,
    action: '積' | '降',
    changed: boolean,
  ) => PlanningsOperationEntity = useCallback((
    orderEntity,
    operationId,
    startAt,
    shiftTruckId,
    action,
    changed,
  ) => {
    const shipperName = orderEntity.shipper_name;
    const itemCount = orderEntity.item_count;
    const itemTotalVolumeMm3 = numberUtil.convertFromM3ToMm3(
      orderEntity.item_total_volume_m3
    );
    const itemTotalWeightForCalculation = numberUtil
      .convertFromKgToGram(
        orderEntity.item_total_weight_kg
      );
    const loadingName = orderEntity.loading_name;
    const loadingAddress = orderEntity.loading_address;
    const unloadingName = orderEntity.unloading_name;
    const unloadingAddress = orderEntity.unloading_address;
    const orderId = orderEntity.id;
    const drivingDurationSeconds = 0;
    const drivingDistanceMm = 0;
    const waitingDurationSeconds = 0;
    const minLoadingStartAt = orderEntity.loading_start_at;
    const maxLoadingEndAt = orderEntity.loading_end_at;
    const minUnloadingStartAt = orderEntity.unloading_start_at;
    const maxUnloadingEndAt = orderEntity.unloading_end_at;
    const arrivalAt = startAt.toISOString();
    const stayingSeconds = action === '積'
      ? orderEntity.loading_staying_minutes
      : orderEntity.unloading_staying_minutes;
    const operationDurationSeconds = numberUtil
      .convertFromMinutesToSeconds(stayingSeconds || 10);
    const departureAt = addSeconds(
      startAt,
      operationDurationSeconds
    ).toISOString();
    const operationStartAt = departureAt;
    const operationWeightG = action === '積'
      ? itemTotalWeightForCalculation
      : itemTotalWeightForCalculation * -1;
    const operationVolumeMm3 = action === '積'
      ? itemTotalVolumeMm3
      : itemTotalVolumeMm3 * -1;
    const latitude = action === '積'
      ? `${orderEntity.loading_lat}`
      : `${orderEntity.unloading_lat}`;
    const longitude = action === '積'
      ? `${orderEntity.loading_lng}`
      : `${orderEntity.unloading_lng}`;
    const delivery = deliveryEntities.find((it) => it.id === shiftTruckId);
    const previousChanged = modifiedOrderIds.includes(orderId);

    return {
      id: operationId,
      shipperName,
      itemName: orderEntity.items.map((it) => it.name).join(', '),
      itemCount,
      itemTotalVolumeMm3,
      itemTotalWeightForCalculation,
      loadingName,
      loadingAddress,
      unloadingName,
      unloadingAddress,
      shiftTruckId,
      orderId,
      arrivalAt,
      departureAt,
      drivingDurationSeconds,
      nullDurationSeconds: 0,
      operationDurationSeconds,
      operationStartAt,
      waitingDurationSeconds,
      drivingDistanceMm,
      action,
      minLoadingStartAt,
      maxLoadingEndAt,
      minUnloadingStartAt,
      maxUnloadingEndAt,
      operationWeightG,
      operationVolumeMm3,
      latitude,
      longitude,
      isChanged: changed,
      originalShiftTruckIds: [],
      cycle: 1,
      orderWasSplit: false,
      detail: [],
      driverId: delivery.driverId,
      truckId: delivery.truckId,
      previousChanged,
      loading_berth_reservations: orderEntity.loading_berth_reservations,
      unloading_berth_reservations: orderEntity.unloading_berth_reservations,
    };
  }, [deliveryEntities, modifiedOrderIds]);

  const buildOperationEvents: (
    operations: (PlanningsOperationEntity[] | undefined),
    shipmentOrderLegRunEntityMap: (Map<number, ShipmentOrderLegRunForPlanEntity> | undefined)
  ) => (LegRunOperationEventEntity[]) = useCallback((
    operations,
    orders
  ) => {
    if (![operations, orders].every((maybe) => maybe)) return [];
    if (!context.config) return [];

    const checkOverTime = (delivery: PlanningsDeliveryEntity, siblings: PlanningsDeliveryEntity[], start: string, end: string) => {
      if (siblings.length > 0) {
        // 連続勤務の場合、どの勤務でも時間内に入っていなければ軽臆する
        return !siblings.some((sibling) => {
          const siblingStart = new Date(sibling.startAt);
          const siblingEnd = new Date(sibling.endAt);
          return (
            siblingStart <= new Date(start) && new Date(end) < siblingEnd
          );
        });
      }

      return new Date(start) < new Date(delivery.startAt) || new Date(delivery.endAt) < new Date(end);
    };

    const checkWiehgtOver = (truck: PlanningsTruckEntity, currentWiehgtG: number) => {
      if (!truck) return false;

      return truck.maximumLoadingCapacityWeightForCalculation < currentWiehgtG;
    };

    const checkVolumeOver = (truck: PlanningsTruckEntity, currentVolumeMm3: number) => {
      if (!truck) return false;

      return (truck.loadingPlatformVolumeMm3 || 0) < (currentVolumeMm3 || 0);
    };

    const checkOutsideSpecifiedTime = (gOps: PlanningsOperationEntityWithStatsEntity[], start: string, end: string) => {
      const startDt = new Date(start);
      const endDt = new Date(end);

      return gOps.some((it) => {
        const maxStartAt = it.action === '積' ? it.minLoadingStartAt : it.minUnloadingStartAt;
        const maxEndAt = it.action === '積' ? it.maxLoadingEndAt : it.maxUnloadingEndAt;
        return new Date(maxEndAt) < startDt || endDt < new Date(maxStartAt);
      });
    };

    const model = new Operations(context.config, operations);
    const operationsByDeliveryId: PlanningsOperationEntitiesWithStatsByDeliveryIdEntity = model
      .groupAndSortEntitiesByDeliveryIdWithVolumeAndWeightIdx({ separateAction: true });

    return Object.keys(operationsByDeliveryId).flatMap((deliveryId) => {
      const ops: PlanningsOperationEntityWithStatsEntity[] = operationsByDeliveryId[`${deliveryId}`];
      const groupedOperations: PlanningsOperationEntityWithStatsEntity[][] = new OperationsWithStatusModel(ops).groupByIdx();

      return groupedOperations.flatMap((gOps) => {
        const colorBuilder = (groupOps: PlanningsOperationEntityWithStatsEntity[]) => {
          const actions: ('積' | '降')[] = groupOps.map((it) => it.action);
          const changed: boolean = groupOps.some((it) => it.isChanged);
          const previousChanged = groupOps.some((it) => it.previousChanged);

          if (actions.every((it) => it === '積')) {
            if (changed) return loadColorLight;
            if (previousChanged) return darken(loadColor, 0.15);
            return loadColor;
          }
          if (actions.every((it) => it === '降')) {
            if (changed) return unloadColorLight;
            if (previousChanged) return darken(unloadColor, 0.15);
            return unloadColor;
          }

          if (changed) return lighten(loadUnloadMixColor, 0.25);
          if (previousChanged) return darken(loadUnloadMixColor, 0.1);
          return loadUnloadMixColor;
        };

        const borderColorBuilder = (groupOps: PlanningsOperationEntityWithStatsEntity[], hasWarning: boolean) => {
          if (hasWarning) {
            return 'red';
          }
          if (groupOps.some((it) => it.isChanged)) {
            return '#000';
          }
          if (groupOps.some((it) => it.previousChanged)) {
            return '#000';
          }
          return colorBuilder(groupOps);
        };

        const id = gOps.map((it) => [
          it.orderId,
          it.action
        ].join('-')).join('-');
        const title = gOps[0]?.action === '積' ? gOps[0]?.loadingName : gOps[0]?.unloadingName;
        const start = arrayUtil.minAt(gOps.map((it) => new Date(it.operationStartAt))).toISOString();
        const end = arrayUtil.maxAt(gOps.map((it) => new Date(it.departureAt))).toISOString();
        const delivery = deliveryEntities.find((it) => it.id === gOps[0].shiftTruckId);
        if (!delivery) return null;

        const resourceId = buildTimelineResourceId(delivery.truckId, delivery.driverId, delivery.id);
        const opsOnPos = gOps.map((it) => ({
          operationId: it.id,
          order: shipmentOrderLegRunEntityMap.get(it.orderId),
          action: it.action
        }));
        const backgroundColor = colorBuilder(gOps);
        const position: PositionEntity = [Number(gOps[0]?.latitude), Number(gOps[0]?.longitude)];
        const { currentWeightG, currentVolumeMm3 } = gOps[0];
        const currentOperationsByDeliveryId = { [`${deliveryId}`]: gOps };
        const details: LegRunOperationEventEntity[] = gOps.flatMap((it) => it.detail).filter((it) => it).filter((it) => it[2] > 0 && it[0] !== 'load').map((it, idx) => {
          let [detailId, detailTitle, detailType, displayType, color] = ['', '', '', '', 'transparent'];
          if (it[0] === 'sleep_rest' || it[0] === 'sleep') {
            [detailId, detailTitle, detailType, displayType, color] = [`${id}-${idx}-sleep-time`, '休息時間', 'operationSleepingEvent', it[0], gOps[0].isChanged ? waittimeColorLight : waittimeColor];
          }
          const detailStart = new Date(it[1]).toISOString();
          const detailEnd = addSeconds(new Date(it[1]), it[2]).toISOString();

          return {
            id: detailId,
            title: detailTitle,
            start: detailStart,
            end: detailEnd,
            editable: false,
            resourceId,
            deliveryId: delivery.id,
            operations: opsOnPos,
            planningsOperations: gOps,
            backgroundColor: color,
            borderColor: color,
            position,
            currentWeightG,
            currentVolumeMm3,
            name: title,
            operationStart: detailStart,
            operationEnd: detailEnd,
            type: detailType,
            operationsByDeliveryId: currentOperationsByDeliveryId,
            baseOperation: null,
            displayType,
            overtime: false,
            weightOver: false,
            volumeOver: false,
            outsideSpecifiedTime: false,
            outsideBerthReservationTime: false,
            hasWarning: false,
          };
        });

        const truck = truckData?.find((it) => it.id === delivery.truckId);

        const siblings = delivery.continuousDeliveryIds && delivery.continuousDeliveryIds.length > 0
                            ? deliveryEntities.filter((it) => delivery.continuousDeliveryIds.includes(it.id)) : [];
        const overtime = checkOverTime(delivery, siblings, start, end);
        const weightOver = checkWiehgtOver(truck, currentWeightG);
        const volumeOver = checkVolumeOver(truck, currentVolumeMm3);
        const outsideSpecifiedTime = checkOutsideSpecifiedTime(gOps, start, end);
        const outsideBerthReservationTime = isOutsideBerthReservationTime(gOps, start, end);
        const hasWarning = overtime || weightOver || volumeOver || outsideSpecifiedTime || outsideBerthReservationTime;

        const borderColor = borderColorBuilder(gOps, hasWarning);

        const ret: LegRunOperationEventEntity[] = details.concat([{
          id,
          title,
          start,
          end,
          editable: true,
          resourceId,
          deliveryId: Number(deliveryId),
          operations: opsOnPos,
          planningsOperations: gOps,
          backgroundColor,
          borderColor,
          position,
          currentWeightG,
          currentVolumeMm3,
          name: title,
          operationStart: start,
          operationEnd: end,
          type: 'operationEvent',
          operationsByDeliveryId: currentOperationsByDeliveryId,
          baseOperation: null,
          displayType: null,
          overtime,
          weightOver,
          volumeOver,
          outsideSpecifiedTime,
          outsideBerthReservationTime,
          hasWarning,
        }]);

        return ret;
      });
    }).filter((it) => it);
  }, [context.config, loadColor, loadColorLight, unloadColor, unloadColorLight, loadUnloadMixColor, waittimeColor, waittimeColorLight, deliveryEntities, truckData, shipmentOrderLegRunEntityMap]);

  const findPairedOperation: (
    operation: PlanningsOperationEntity,
    operations: PlanningsOperationEntity[],
  ) => PlanningsOperationEntity | undefined = (
    operation,
    operations,
  ) => operations.filter((it) => {
    const act = operation.action === '積' ? '降' : '積';

    return [
      it.action === act,
      it.orderId === operation.orderId
    ].every((bool) => bool);
  })[0];

  const draggableElementId = 'external-events';
  const draggableElementClassName = 'external-event';

  const [operationEntities, dispatchOperationEntities] = useReducer(operationEntitiesReducer, []);
  const [operationEvents, dispatchOperationEvents] = useReducer(legRunOperationEventsReducer, []);

  useEffect(() => {
    const updateWidth = (): void => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', updateWidth);
    updateWidth();

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    const drawerWidth = openUnallocatedOrders ? unallocatedDrawerWidth : 0;
    const contentWidth = windowWidth - drawerWidth;

    if (contentWidth <= 0) return;

    if (contentWidth <= theme.breakpoints.values.md) {
      enqueueSnackbar('画面幅が狭いため、アプリケーションの操作が制限されます。', { variant: 'warning' });
    }
  }, [windowWidth, openUnallocatedOrders, theme, unallocatedDrawerWidth, enqueueSnackbar]);

  useEffect(() => {
    setDisplayOrderDialogIsOpen(!!displayDetailOrderId);
  }, [displayDetailOrderId]);

  const addEventDialogSelectedOrderIds = useCallback((eventDialogOrderIds: number) => {
    setEventDialogSelectedOrderIds((prev) => {
      if (prev.includes(eventDialogOrderIds)) {
        return prev.filter((it) => it !== eventDialogOrderIds);
      }
      return [...prev, eventDialogOrderIds];
    });
  }, []);

  const resetDisplayDetailOrderId = useCallback(() => {
    setDetailDisplayOrderId(undefined);
  }, []);

  const deleteButtonOnClick = useCallback(() => {
    if (isLoading) return;
    if (!eventDialogSelectedOrderIds.length) return;

    setConfirmDialogMessage(`${eventDialogSelectedOrderIds.length}件の配送計画を削除します。よろしいですか?`);
    setConfirmDialogIsOpen(true);
  }, [isLoading, eventDialogSelectedOrderIds]);

  const confirmDialogHandleOk = useCallback(() => {
    setConfirmDialogIsOpen(false);
    const ops = operationEntities.filter((it) => (eventDialogSelectedOrderIds.includes(it.orderId)));

    const savedOperations = ops.filter((it) => it.id);
    const savedOrderIds = savedOperations.map((it) => it.orderId);
    if (savedOrderIds.length > 0) {
      mutateDeleteOrdersOperations(savedOrderIds);
    }
  }, [mutateDeleteOrdersOperations, eventDialogSelectedOrderIds, operationEntities]);

  const confirmDialogHandleCancel = useCallback(() => {
    setConfirmDialogIsOpen(false);
  }, []);

  const planningOrderPresenterMemo = useMemo(() => (
    <Dialog
      fullWidth
      maxWidth="md"
      open={displayOrderDialogIsOpen}
      onClose={resetDisplayDetailOrderId}
    >
      <PlanningOrderPresenter
        orderId={displayDetailOrderId}
        customInputFields={[]}
      />
    </Dialog>
  ), [displayOrderDialogIsOpen, displayDetailOrderId, resetDisplayDetailOrderId]);

  const onClickEvent = useCallback((eventClickArg: EventClickArg) => {
    const { id } = eventClickArg.event;
    const operationEventEntity: LegRunOperationEventEntity = operationEvents.find((it) => it.id === id);

    if (!operationEventEntity || operationEventEntity.type !== 'operationEvent') return;

    setSelectedOperation(operationEventEntity);
    setAnchorEl(eventClickArg.el); // クリックされた要素を保存
    setEventDialogSelectedOrderIds([]);
  }, [operationEvents]);

  const saveTimelineRef = useRef(saveTimeline);
  useEffect(() => {
    saveTimelineRef.current = saveTimeline;
  }, [saveTimeline]);

  // ユーザー操作の終了を検出するためのdebounce関数を作成
  const debouncedSave = useRef(
    debounce((
      ops: PlanningsOperationEntity[],
      resourceEntities: CalendarResourceEntity[],
      latestAlgorithmVersion: number,
    ) => {
      if (!ops.some((it) => it.isChanged)) return;

      const deliveryIdResourceIdMap = new Map<number, string>(
        resourceEntities.map((it) => [it.deliveryId, it.id])
      );

      enqueueSnackbar('自動保存');
      const changedOperations = ops.filter((it) => it.isChanged);
      const changedResourceIds = arrayUtil.uniq(changedOperations.map((it) => it.shiftTruckId).map((it) => deliveryIdResourceIdMap.get(it)));
      const changedResourceDeliveryIds = resourceEntities.filter((it) => changedResourceIds.includes(it.id)).map((it) => it.deliveryId);
      const changedOriginalResourceIds = arrayUtil.uniq(changedOperations.flatMap((it) => it.originalShiftTruckIds).filter((it) => it).map((it) => deliveryIdResourceIdMap.get(it)));
      const changedOriginalResourceDeliveryIds = resourceEntities.filter((it) => changedOriginalResourceIds.includes(it.id)).map((it) => it.deliveryId);
      const changedDeliveryIds = arrayUtil.uniq(changedResourceDeliveryIds.concat(changedOriginalResourceDeliveryIds));

      const changedDeliveries: PlanningsOperationEntity[] = [];
      const emptyDeliveryIds: number[] = [];
      changedDeliveryIds.forEach((it) => {
        const tmp = ops.filter((op) => op.shiftTruckId === it);
        if (tmp.length === 0) {
          emptyDeliveryIds.push(it);
        } else {
          changedDeliveries.push(...tmp);
        }
      });

      setLatestRequestAlgorithmVersion(latestAlgorithmVersion);
      saveTimelineRef.current(changedDeliveries, emptyDeliveryIds, latestAlgorithmVersion);
      setIsDirty(false);
    }, 1000)
  ).current;

  // 離脱確認で「破棄」を選んだときの後始末。window.confirm がメインスレッドを同期ブロック
  // する間も自動保存の待ち時間は進んでおり、OK でブロックが解けた直後に保存＋自動配車が
  // 走ってしまうため、遷移前にタイマーを止めて保存対象フラグも落とす。
  const discardPendingSave = useCallback(() => {
    debouncedSave.cancel();
    setShouldSave(false);
    setIsDirty(false);
  }, [debouncedSave]);

  useNavigationGuard(isDirty || shouldSave, discardPendingSave);

  // マウスやキーボード操作を監視してdebounceを制御
  useEffect(() => {
    const handleUserInteraction = () => {
      // 既存のdebounceをキャンセルして新しいタイマーを開始
      if (debouncedSave && typeof debouncedSave.cancel === 'function') {
        debouncedSave.cancel();
      }

      // 操作が検出された時点で、変更があればdebounceを開始
      if (operationEntities.some((it) => it.isChanged) && !plansContext.calculating) {
        debouncedSave(
          operationEntities,
          resources,
          plansContext.latestAlgorithmRequestVersion
        );
      }
    };

    // 監視するイベントタイプ
    const eventTypes = [
      'mousedown',
      'mousemove',
      'mouseup',
      'keydown',
      'keyup',
      'touchstart',
      'touchmove',
      'touchend'
    ];
    // const eventTypes = ['mouseup', 'keyup', 'touchend'];

    // イベントリスナーを追加
    eventTypes.forEach((eventType) => {
      document.addEventListener(eventType, handleUserInteraction, { passive: true });
    });

    // クリーンアップ
    return () => {
      eventTypes.forEach((eventType) => {
        document.removeEventListener(eventType, handleUserInteraction);
      });
    };
  }, [debouncedSave, operationEntities, resources, plansContext.calculating, plansContext.latestAlgorithmRequestVersion]);

  // コンポーネントのクリーンアップ時にdebounceをキャンセル
  useEffect(() => () => {
    debouncedSave.cancel();
  }, [debouncedSave]);

  const validateAndSave = useCallback((
    changeArg: EventChangeArg,
    newOperations: PlanningsOperationEntity[],
  ) => {
    let valid = true;
    const validatedOperations = newOperations.map((newOperation) => {
      if (!valid) return null;

      const pairedOperation = findPairedOperation(newOperation, operationEntities);

      if (pairedOperation) {
        if (newOperation.action === '積') {
          if (new Date(newOperation.departureAt).getTime() > new Date(pairedOperation.arrivalAt).getTime()) {
            // 積の場合、降より先にある
            enqueueSnackbar('積地の時間を降地より後ろにできません。先に降地の時間を変更してください。');
            valid = false;
            return null;
          }
        }
        if (newOperation.action === '降') {
          if (new Date(newOperation.arrivalAt).getTime() < new Date(pairedOperation.departureAt).getTime()) {
            // 降の場合、積より後にある
            enqueueSnackbar('降地の時間を積地より前にできません。先に積地の時間を変更してください。');
            valid = false;
            return null;
          }
        }
      }

      return newOperation;
    });
    // だめなものがあったら変更しない
    if (validatedOperations.some((it) => !it)) {
      changeArg.revert();
      return false;
    }

    newOperations.forEach((it) => {
      dispatchOperationEntities({
        type: 'update',
        payload: it,
      });
    });

    return true;
  }, [enqueueSnackbar, operationEntities]);

  const handleSameResource = useCallback((
    changeArg: EventChangeArg,
    currentOperations: PlanningsOperationEntity[],
    operations: PlanningsOperationEntity[],
    startAt: Date,
    endAt: Date,
    destinationDelivery: PlanningsDeliveryEntity,
  ) => {
    const oldStartAt = new Date(currentOperations[0].arrivalAt);
    const oldEndAt = new Date(currentOperations[0].departureAt);
    const startIsEdited = oldStartAt.getTime() !== startAt.getTime();
    const endIsEdited = oldEndAt.getTime() !== endAt.getTime();
    const newOperationDuration = (endAt.getTime() - startAt.getTime()) / 1000;
    const newOperations: PlanningsOperationEntity[] = currentOperations.map((it) => ({
      ...it,
      shiftTruckId: destinationDelivery.id,
      arrivalAt: datetimeDecorator.fmtIso8601(startAt),
      operationStartAt: datetimeDecorator.fmtIso8601(startAt),
      departureAt: datetimeDecorator.fmtIso8601(endAt),
      operationDurationSeconds: newOperationDuration,
      waitingDurationSeconds: 0,
      isChanged: true,
      detail: it.detail?.map((d) => {
        if (!startIsEdited || d[2] === 0) {
          return d;
        }
        const diffTime = (oldStartAt.getTime() - startAt.getTime()) / 1000;
        return [d[0], d[1], d[2] - diffTime];
      }),
    }));

    const valid = validateAndSave(changeArg, newOperations);
    if (!valid) {
      return false;
    }
    newOperations.forEach((it) => {
      dispatchOperationEntities({
        type: 'update',
        payload: it,
      });
    });

    const tempOperations = operations
      .filter((it) => it.driverId === currentOperations[0].driverId && it.truckId === currentOperations[0].truckId)
      .sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime());
    const currentIndex = tempOperations.findIndex((it) => it.id === currentOperations[0].id);
    if (endIsEdited) {
      const nextIndex = currentIndex + currentOperations.length;
      const nextOperation = tempOperations[nextIndex];
      if (!nextOperation) {
        return true;
      }

      dispatchOperationEntities({
        type: 'update',
        payload: {
          ...nextOperation,
          detail: nextOperation.detail?.map((d) => {
            if (new Date(d[1]).getTime() === oldEndAt.getTime()) {
              const diffTime = (endAt.getTime() - oldEndAt.getTime()) / 1000;
              return [d[0], endAt.toISOString(), d[2] - diffTime];
            }
            return d;
          })
        }
      });
    }
    return true;
  }, [validateAndSave]);

  const handleOtherResource = useCallback((
    changeArg: EventChangeArg,
    currentOperations: PlanningsOperationEntity[],
    operations: PlanningsOperationEntity[],
    startAt: Date,
    endAt: Date,
    resourceId: string,
    deliveryId: number,
  ) => {
    const diffSeconds = (changeArg.event.start.getTime() - changeArg.oldEvent.start.getTime()) / 1000;

    const newOperations: PlanningsOperationEntity[] = currentOperations.map((it) => ({
      ...it,
      arrivalAt: datetimeDecorator.fmtIso8601(startAt),
      operationStartAt: datetimeDecorator.fmtIso8601(startAt),
      departureAt: datetimeDecorator.fmtIso8601(endAt),
      shiftTruckId: Number(deliveryId),
      waitingDurationSeconds: 0,
      originalShiftTruckIds: [...(it.originalShiftTruckIds || []), it.shiftTruckId],
      isChanged: true,
      resourceId,
      detail: [],
    }));

    const pairedOperations: PlanningsOperationEntity[] = newOperations.map((newOperation) => {
      const pairedOperation = findPairedOperation(newOperation, operations);

      if (pairedOperation) {
        const newArrivalAt = addSeconds(new Date(pairedOperation.arrivalAt), diffSeconds);

        const order = shipmentOrderLegRunEntityMap.get(pairedOperation.orderId);
        const diffMinutes = pairedOperation.action === '積' ? order.loading_staying_minutes : order.unloading_staying_minutes;
        const newDepartureAt = addMinutes(newArrivalAt, diffMinutes);

        const resourceIds = changeArg.event.getResources().map((it) => it.id);
        const droppeddResources = resources.filter((it) => resourceIds.includes(it.id));
        const droppedDeliveryIds = droppeddResources.map((it) => it.deliveryId);
        const droppedDeliveries = deliveryEntities.filter((it) => droppedDeliveryIds.includes(it.id));
        const newDelivery = findClosestDelivery(droppedDeliveries, newArrivalAt);

        return {
            ...pairedOperation,
            arrivalAt: datetimeDecorator.fmtIso8601(newArrivalAt),
            operationStartAt: datetimeDecorator.fmtIso8601(newArrivalAt),
            departureAt: datetimeDecorator.fmtIso8601(newDepartureAt),
            shiftTruckId: Number(newDelivery.id),
            originalShiftTruckId: [...(pairedOperation.originalShiftTruckIds || []), pairedOperation.shiftTruckId],
            waitingDurationSeconds: 0,
            isChanged: true,
            resourceId,
            detail: [],
        };
      }

      return null;
    });

    if (pairedOperations.some((it) => !it)) {
      // なにかエラーがあるので戻す
      changeArg.revert();
      return false;
    }

    newOperations.forEach((newOperation) => {
      dispatchOperationEntities({
        type: 'update',
        payload: newOperation
      });
    });
    pairedOperations.forEach((pairedOperation) => {
      dispatchOperationEntities({
        type: 'update',
        payload: pairedOperation
      });
    });

    return true;
  }, [deliveryEntities, resources, shipmentOrderLegRunEntityMap]);

  const findClosestDelivery = (deliveries: PlanningsDeliveryEntity[], startAt: Date,) => {
    const targetStart = new Date(startAt);
    const targetDateStr = targetStart.toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' });

    // 1. 同じ日付のものを絞り込み
    const sameDateRanges = deliveries.filter((w) => {
      const wDate = new Date(w.startAt).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' });
      return wDate === targetDateStr;
    });

    const rangesToCheck = sameDateRanges.length > 0 ? sameDateRanges : deliveries;
    let closestDelivery: PlanningsDeliveryEntity | null = null;
    let closestDiff = Infinity;

    rangesToCheck.forEach((w) => {
      const wStart = new Date(w.startAt);
      const wEnd = new Date(w.endAt);

      // 差の計算（targetStart からの距離）
      const diff = Math.min(
        Math.abs(targetStart.getTime() - wStart.getTime()),
        Math.abs(targetStart.getTime() - wEnd.getTime())
      );

      if (diff < closestDiff) {
        closestDiff = diff;
        closestDelivery = w;
      }
    });

    return closestDelivery;
  };

  const handleIndexChanges = useCallback((
    changeArg: EventChangeArg,
    operationEventsInResource: LegRunOperationEventEntity[],
    oldIndex: number,
    newIndex: number,
  ) => {
    const reorderedOperationEvents = [...operationEventsInResource];
    const [draggedEvent] = reorderedOperationEvents.splice(oldIndex, 1);
    reorderedOperationEvents.splice(newIndex, 0, draggedEvent);

    const newOperations: PlanningsOperationEntity[] = reorderedOperationEvents.flatMap((operation, index) => {
      let newStart: Date = null;
      let newEnd: Date = null;
      if (operation.id === changeArg.event.id) {
        newStart = changeArg.event.start;
        newEnd = changeArg.event.end;
      }

      const operationsToUpdate = operationEntities.filter((op) => (
        operation.operations.some((eventOp) => eventOp.action === op.action && eventOp.order?.id === op.orderId)
      ));

      return operationsToUpdate.map((op) => ({
        ...op,
        arrivalAt: newStart ? datetimeDecorator.fmtIso8601(newStart) : op.arrivalAt,
        operationStartAt: newStart ? datetimeDecorator.fmtIso8601(newStart) : op.operationStartAt,
        departureAt: newEnd ? datetimeDecorator.fmtIso8601(newEnd) : op.departureAt,
        operationDurationSeconds: (newStart && newEnd) ? (newEnd.getTime() - newStart.getTime()) / 1000 : op.operationDurationSeconds,
        waitingDurationSeconds: 0,
        isChanged: true,
      }));
    });

    return validateAndSave(changeArg, newOperations);
  }, [operationEntities, validateAndSave]);

  // operationEntitiesの変更を監視して保存を実行
  useEffect(() => {
    if (shouldSave && operationEntities.some((it) => it.isChanged) && !plansContext.calculating && (
      !latestRequestAlgorithmVersion || latestRequestAlgorithmVersion < plansContext.latestAlgorithmRequestVersion
    )) {
      setIsDirty(true);
      debouncedSave(
        operationEntities,
        resources,
        plansContext.latestAlgorithmRequestVersion
      );
      setShouldSave(false);
    }
  }, [operationEntities, operationEvents, plansContext, debouncedSave, shouldSave, resources, latestRequestAlgorithmVersion]);

  // シフトの会社を解決する。ドライバー未設定シフトでも会社比較できるよう、ドライバーの会社ではなく
  // トラック(→車庫)の会社を使う。truckId===-1（「指定なし」仮想レーン）は virtual として区別する
  // （詳細は companyGuard.ts のコメント参照）。
  const resolveDeliveryCompanyResolution = useCallback((delivery?: PlanningsDeliveryEntity) => (
    resolveDeliveryCompany(delivery, truckData, garageData)
  ), [truckData, garageData]);

  const onChange = useCallback((changeArg: EventChangeArg) => {
    const beforeResourceId = changeArg.oldEvent.getResources()[0].id;
    const destinationResourceId = changeArg.event.getResources()[0].id;

    const { start: newStartAt, end: newEndAt, id } = changeArg.event;
    const { extendedProps: destinationEvent } = changeArg.event.toPlainObject() as {
      extendedProps: OperationEventEntity;
    };
    const { operations, type, deliveryId }: OperationEventEntity = destinationEvent;

    if (type !== 'operationEvent') {
      changeArg.revert();
      enqueueSnackbar('変更できません');
      return;
    }

    const currentOperations: PlanningsOperationEntity[] = operations
      .map((opEventOp) => operationEntities.find((it) => it.action === opEventOp.action && it.orderId === opEventOp.order?.id))
      .filter((maybe) => maybe);

    const destinationResources = resources.filter((it) => it.id === destinationResourceId);
    const destinationDeliveryIds = destinationResources.map((it) => it.deliveryId);
    const destinationDeliverys = deliveryEntities.filter((it) => destinationDeliveryIds.includes(it.id));
    const destinationDelivery = findClosestDelivery(destinationDeliverys, newStartAt);

    const sourceFixed = fixedShiftTruckIds.includes(deliveryId);
    const destinationFixed = destinationDelivery.fixed;
      if (sourceFixed || destinationFixed) {
      changeArg.revert();
      enqueueSnackbar('確定した計画は変更できません');
      return;
    }

    const beforeDelivery = deliveryEntities.find((it) => it.id === deliveryId);
    const destinationDriver = driverEntities.find((it) => it.id === destinationDelivery.driverId);

    // 会社判定はドライバーではなくシフト(トラック→車庫)の会社で行う（ドライバー未設定シフトへも移動可能にする）。
    // サーバ側(TimelinePlanningJob)には配置元/配置先の会社を突合するチェックが無く、company scope による
    // 暗黙フィルタ止まりのため、このFEガードが唯一の防波堤。会社IDが解決できない(truck/garage未取得等)場合は
    // 「未割当だから許可」と誤認せず拒否側に倒す。ただし「指定なし」仮想レーン(truckId===-1)は
    // 会社チェック対象外として許可する（companyGuard.ts の isCrossCompanyMove 参照）。
    const beforeCompanyResolution = resolveDeliveryCompanyResolution(beforeDelivery);
    const destinationCompanyResolution = resolveDeliveryCompanyResolution(destinationDelivery);
    if (isCrossCompanyMove(beforeCompanyResolution, destinationCompanyResolution)) {
      changeArg.revert();
      enqueueSnackbar('別の事業所の案件は設定できません');
      return;
    }

    currentOperations.forEach((it) => { it.isChanged = true; });

    let saved = false;
    if (beforeResourceId === destinationResourceId) {
      const eventsInSameResource = operationEvents.filter((it) => it.resourceId === destinationResourceId);
      const operationEventsInResource = eventsInSameResource.filter((it) => it.type === 'operationEvent');
      const oldIndex = operationEventsInResource.findIndex((it) => it.id === id);
      const tempEvents = [...operationEventsInResource];
      tempEvents.splice(oldIndex, 1);
      let newIndex = tempEvents.findIndex((it) => newStartAt.getTime() < new Date(it.start).getTime());
      if (newIndex === -1) {
        newIndex = tempEvents.length;
      }

      if (destinationDriver?.id === tempEvents[0].deliveryId && oldIndex !== newIndex) {
        saved = handleIndexChanges(
          changeArg,
          operationEventsInResource,
          oldIndex,
          newIndex,
        );
      } else {
        saved = handleSameResource(
          changeArg,
          currentOperations,
          operationEntities,
          new Date(newStartAt),
          new Date(newEndAt),
          destinationDelivery,
        );
      }
    } else {
      saved = handleOtherResource(
        changeArg,
        currentOperations,
        operationEntities,
        new Date(newStartAt),
        new Date(newEndAt),
        destinationResourceId,
        destinationDelivery.id,
      );
    }

    setShouldSave(saved);
  }, [resources, deliveryEntities, fixedShiftTruckIds, operationEntities, deliveryEntities, driverEntities, operationEvents, handleSameResource, handleOtherResource, handleIndexChanges, resolveDeliveryCompanyResolution]);

  const onDrop = useCallback((arg: DropArg) => {
    if (isLoading) {
      return;
    }
    // カレンダー内のイベントの場合は処理をスキップ
    if (arg.draggedEl.classList.contains('fc-event')) {
      console.log('Calendar event detected in onDrop - skipping');
      return;
    }

    const { resource: { id }, draggedEl: { dataset: { entity: json, groupedids } }, date: loadingStart } = arg;
    if (!json) {
      arg.draggedEl.style.display = 'block';
      // enqueueSnackbar('移動処理でエラーがありました。');
      return;
    }
    const resourceId = id;
    const orderEntity = JSON.parse(json) as ShipmentOrderLegRunForPlanEntity;

    let unloadingStart = addMinutes(loadingStart, (orderEntity.loading_staying_minutes || 10) + 30); // 走行時間30分
    const unloadingSpecifiedTime = orderEntity.loading_specified_datetimes_attributes
      .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
      .find((it) => loadingStart.getTime() < new Date(it.end_at).getTime());
    if (unloadingSpecifiedTime && unloadingStart.getTime() < new Date(unloadingSpecifiedTime.start_at).getTime()) {
      unloadingStart = new Date(unloadingSpecifiedTime.start_at);
    }

    const droppedResources = resources.filter((it) => it.id === resourceId);
    const droppedResourceDeliveryIds = droppedResources.map((it) => it.deliveryId);
    const droppedDeliveries = deliveryEntities.filter((it) => droppedResourceDeliveryIds.includes(it.id));
    const delivery = findClosestDelivery(droppedDeliveries, loadingStart);
    const unloadingDelivery = findClosestDelivery(droppedDeliveries, unloadingStart);

    if (!unloadingDelivery) {
      enqueueSnackbar('対応する勤務計画がありません。');
      return;
    }

    const sourceFixed = fixedShiftTruckIds.includes(Number(delivery.id)) || fixedShiftTruckIds.includes(Number(unloadingDelivery.id));
    if (sourceFixed) {
      enqueueSnackbar('確定した計画は変更できません。');
      return;
    }

    // 会社判定はドライバーではなくシフト(トラック→車庫)の会社で行う（ドライバー未設定シフトへも配置可能にする）。
    // サーバ側(TimelinePlanningJob)には配置元/配置先の会社を突合するチェックが無く、company scope による
    // 暗黙フィルタ止まりのため、このFEガードが唯一の防波堤。会社IDが解決できない(truck/garage未取得等)場合は
    // 「未割当だから許可」と誤認せず拒否側に倒す。ただし「指定なし」仮想レーン(truckId===-1)は
    // 会社チェック対象外として許可する（companyGuard.ts の isCrossCompanyDrop 参照）。
    const shiftCompanyResolution = resolveDeliveryCompanyResolution(unloadingDelivery);
    if (isCrossCompanyDrop(shiftCompanyResolution, orderEntity.company_id)) {
      enqueueSnackbar('別の事業所の案件は設定できません。');
      return;
    }

    const unloadingOperation = buildOperationEntity(
      orderEntity,
      0,
      unloadingStart,
      unloadingDelivery.id,
      '降',
      true,
    );

    dispatchOperationEntities({
      type: 'add',
      payload: unloadingOperation,
    });

    const loadingOperation: PlanningsOperationEntity = buildOperationEntity(
      orderEntity,
      0,
      loadingStart,
      delivery.id,
      '積',
      true,
    );

    dispatchOperationEntities({
      type: 'add',
      payload: loadingOperation,
    });

    const updatedOrderIds = [orderEntity.id];
    if (groupedids.length > 0) {
      groupedids.split(',').forEach((it) => {
        updatedOrderIds.push(Number(it));
        const entity = shipmentOrderLegRunEntityMap.get(Number(it));
        if (!entity) return;

        dispatchOperationEntities({
          type: 'add',
          payload: buildOperationEntity(
            entity,
            0,
            unloadingStart,
            unloadingDelivery.id,
            '降',
            true,
          )
        });

        dispatchOperationEntities({
          type: 'add',
          payload: buildOperationEntity(
            entity,
            0,
            loadingStart,
            delivery.id,
            '積',
            true,
          ),
        });
      });
    }
    orderSelection.addAllocatedOrderIds(updatedOrderIds);
    setShouldSave(true);
  }, [buildOperationEntity, fixedShiftTruckIds, isLoading, shipmentOrderLegRunEntityMap, resources, deliveryEntities, orderSelection, resolveDeliveryCompanyResolution]);

  const handlePopoverOpen = useCallback((hoverArg: EventHoveringArg) => {
    const { el, event, jsEvent } = hoverArg;
    const { type }: { type?: string } = event.extendedProps;

    if (['operationDrivingEvent', 'operationSleepingEvent', 'operationRestingEvent', 'operationEvent', 'operationWaitingEvent', 'baseOperationEvent'].includes(type)) {
      setPopoverEl(el);
      setPopoverOperationEventEntity(event);
      setMousePosition({
        x: jsEvent.clientX,
        y: jsEvent.clientY,
      });
    }
  }, []);

  const handlePopoverClose = useCallback(() => {
    setPopoverEl(null);
    setPopoverOperationEventEntity(null);
    setMousePosition(null);
  }, []);

  const createOperationData = useCallback((deliveries: PlanningsDeliveryEntity[]) => (
    deliveries.flatMap((it) => {
      let prev: PlanningsOperationEntity = null;
      return it.operations.map((op) => {
        if (prev && prev.action === op.action && prev.latitude === op.latitude && prev.longitude === op.longitude && prev.departureAt === op.departureAt) {
          // 同一地点の場合、最初以外の所要時間が0になっている。他のDeliveryに移動された場合に元々の時間がわからなくなるので、すべてに同じ開始・終了時間を設定しておく。
          prev = {
            ...op,
            arrivalAt: prev.arrivalAt,
            departureAt: prev.departureAt,
            operationDurationSeconds: prev.operationDurationSeconds,
            operationStartAt: prev.operationStartAt,
          };
          return prev;
        }
        prev = op;
        return {
          ...op,
          previousChanged: modifiedOrderIds.includes(op.orderId),
        };
      });
    })
  ), [modifiedOrderIds]);

  useEffect(() => {
    if (![deliveryEntities].every((maybe) => maybe)) return;
    if (shouldSave || isDirty || plansContext.calculating) return;

    const operationData = createOperationData(deliveryEntities);
    dispatchOperationEntities(
      {
        type: 'set',
        payload: operationData,
      }
    );
  }, [createOperationData, deliveryEntities, isDirty, shouldSave]);

  useEffect(() => {
    if (![shipmentOrderLegRunEntityMap, operationEntities].every((maybe) => maybe)) return;

    dispatchOperationEvents(
      {
        type: 'set',
        payload: buildOperationEvents(operationEntities, shipmentOrderLegRunEntityMap)
      }
    );
  }, [buildOperationEvents, operationEntities, shipmentOrderLegRunEntityMap]);

  useEffect(() => {
    setEvents([
      ...operationEvents.map((it) => ({ ...it, droppable: false, })),
      ...deliveryEvents,
    ]);
  }, [operationEvents, deliveryEvents]);

  const getSlotMinTime = useCallback(() => {
    if (deliveryEvents.length === 0) return '00:00:00';

    const minStart = arrayUtil.minAt(deliveryEvents.map((it) => new Date(it.start)));
    if (minStart) {
      return `${minStart.getHours()}:00:00`;
    }
    return '00:00:00';
  }, [deliveryEvents]);

  const calendarDuration = useMemo(() => (
    { days: calendarDurationDays(startDate, endDate) }
  ), [startDate, endDate]);

  // ビュー変更時のハンドラを追加
  const handleViewChange = useCallback((view: string) => {
    localStorage.setItem(LOCAL_STORAGE_VIEW_KEY, view);
    setInitialView(view);
  }, []);

  const Views = useMemo(() => ({
    resourceTimelineDay: {
      buttonText: '拡大',
      slotDuration: calendarDuration.days > 6 ? '00:15' : '00:10',
      duration: calendarDuration,
    },
    resourceTimelineDayTwo: {
      type: 'resourceTimelineDay',
      buttonText: '標準',
      slotDuration: calendarDuration.days > 6 ? '00:20' : '00:15',
      duration: calendarDuration,
    },
    resourceTimelineDayThree: {
      type: 'resourceTimelineDay',
      buttonText: '縮小',
      slotDuration: '01:00',
      duration: calendarDuration,
    }
  }), [calendarDuration]);

  const HeaderToolbar = useMemo(() => ({
    left: '', // 'displayButtons.join(' ')',
    center: 'title',
    right: 'resourceTimelineDay,resourceTimelineDayTwo,resourceTimelineDayThree'
  }), []);

  const canDisplay = useCallback((res: CalendarResourceEntity) => {
    const truckIds = selectedGroupEntities.filter((it) => it).flatMap((it) => it.truckIds);

    if (!truckIds.includes(res.truckEntity.id)) return false;

    switch (truckDisplayStatus) {
      case '配車済み車両のみ表示':
        return res.hasOperations;
      case '未配車の車両のみ表示':
        return !res.hasOperations;
      case '選択中のみ表示する':
        return checkedResources.includes(res.id);
      default:
        return true;
    }
  }, [checkedResources, truckDisplayStatus, selectedGroupEntities]);

  useEffect(() => {
    if (checkedResources.length === 0) {
      setSelectedState('none');
    } else if (resources.filter((it) => canDisplay(it)).map((it) => it.id).every((it) => checkedResources.includes(it))) {
      setSelectedState('every');
    } else {
      setSelectedState('some');
    }
  }, [checkedResources, resources, canDisplay]);

  const handleCheckboxChange = useCallback((event: React.ChangeEvent<HTMLElement>, resourceId: string) => {
    event.preventDefault();
    event.stopPropagation();

    setCheckedResources((prev) => (prev.includes(resourceId)
        ? prev.filter((id) => id !== resourceId)
        : [...prev, resourceId]));
  }, []);

  const handleTruckLabelClick = useCallback((event: React.MouseEvent<HTMLElement>, resourceId: string) => {
    event.preventDefault();
    event.stopPropagation();

    const clickedResources = resources.filter((it) => it.id === resourceId);

    const deliveryInfo = deliveryEntities.find((it) => it.id === clickedResources[0].deliveryId);
    if (deliveryInfo) {
      setSelectedTruckInfo(deliveryInfo);
      setInfoPopoverAnchorEl(event.currentTarget);
    }
  }, [deliveryEntities, resources]);

  // 情報パネル用のクローズハンドラ
  const handleInfoPanelClose = useCallback(() => {
    setInfoPopoverAnchorEl(null);
  }, []);

  // 情報パネルUIコンポーネント
  const truckInfoPanel = useMemo(() => {
    if (!selectedTruckInfo || !truckData || !garageData || !driverEntities || !deliveryEntities) return null;

    const truck = truckData.find((t) => t.id === selectedTruckInfo.truckId);
    if (!truck) return null;

    const garage = garageData.find((g) => g.id === truck.garageId);

    // ドライバー未設定シフトでもパネルを開く（driver は null 可。TruckInfoPanel 側は driver && でガード済み）。
    const driver = driverEntities.find((d) => d.id === selectedTruckInfo.driverId) ?? null;

    const deliveries = deliveryEntities.filter((it) => it.driverId === selectedTruckInfo.driverId && it.truckId === truck.id);

    return (
      <TruckInfoPanel
        truck={truck}
        garage={garage}
        driver={driver}
        deliveries={deliveries}
        infoPopoverAnchorEl={infoPopoverAnchorEl}
        handleInfoPanelClose={handleInfoPanelClose}
      />
    );
  }, [selectedTruckInfo, truckData, garageData, driverEntities, deliveryEntities, infoPopoverAnchorEl, handleInfoPanelClose]);

  useEffect(() => {
    setTruckIdsHasDelivery(deliveryEntities.map((it) => it.truckId));
    setDriverIdsHasDelivery(deliveryEntities.map((it) => it.driverId));
  }, [deliveryEntities]);

  const handleTruckClick = useCallback((event: React.MouseEvent, id: number, licensePlateValue: string) => {
    event.preventDefault();
    event.stopPropagation();

    const deliveryIds = deliveryEntities.filter((it) => it.truckId === truckEntity.id).map((it) => it.id);

    assignTruckToTemporaryDeliveryRequest({
      deliveryIds,
      truckId: id,
      origTruckId: truckEntity.id,
      licensePlateValue,
      driverId: null,
      driverName: null,
      origDriverId: null,
    });
    setTruckAnchorEl(null);
    setTruckEntity(null);
  }, [deliveryEntities, truckEntity]);

  const handleTruckMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, clickedTruckEntity: PlanningsTruckEntity) => {
    event.preventDefault();
    event.stopPropagation();
    setTruckAnchorEl(event.currentTarget);
    setTruckEntity(clickedTruckEntity);
  }, []);

  const handleTruckListClose = useCallback((event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setTruckAnchorEl(null);
    setTruckEntity(null);
  }, []);

  const selectTruckMenuMemo = useMemo(() => (
    <SelectTruckMenuPresenter
      truckAnchorEl={truckAnchorEl}
      onClose={handleTruckListClose}
      onTruckClick={handleTruckClick}
      truckData={truckData as SelectTruckMenuTruckEntity[] || []}
      truckIdsHasDelivery={truckIdsHasDelivery}
      isTemporary={truckEntity?.temporary}
    />
  ), [truckEntity, truckData, handleTruckClick, handleTruckListClose, truckAnchorEl, truckIdsHasDelivery]);

  const handleDriverClick = useCallback((event: React.MouseEvent, id: number, name: string) => {
    event.preventDefault();
    event.stopPropagation();

    // 対象 delivery の決定: 割当済みシフトは従来どおり「そのドライバーの全シフト」を一括変更する。
    // ドライバー未設定シフト(driverId=null)は旧ドライバー id で特定できないため、開いたシフト 1 件のみを対象にする。
    const clickedDelivery = deliveryEntities.find((it) => it.id === driverMenuDeliveryId);
    let deliveryIds: number[];
    if (clickedDelivery && clickedDelivery.driverId != null) {
      deliveryIds = deliveryEntities.filter((it) => it.driverId === clickedDelivery.driverId).map((it) => it.id);
    } else if (driverMenuDeliveryId != null) {
      deliveryIds = [driverMenuDeliveryId];
    } else {
      deliveryIds = [];
    }

    assignTruckToTemporaryDeliveryRequest({
      deliveryIds,
      truckId: null,
      origTruckId: null,
      licensePlateValue: null,
      driverId: id,
      origDriverId: driverEntity?.id ?? null,
      driverName: name,
    });
    setDriverAnchorEl(null);
    setDriverEntity(null);
    setDriverMenuDeliveryId(null);
  }, [deliveryEntities, driverEntity, driverMenuDeliveryId]);

  const handleDriverMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, clickedDriverEntity: PlanningsDriverEntity, deliveryId: number) => {
    event.preventDefault();
    event.stopPropagation();
    setDriverAnchorEl(event.currentTarget);
    setDriverEntity(clickedDriverEntity);
    setDriverMenuDeliveryId(deliveryId);
  }, []);

  const handleDriverListClose = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDriverAnchorEl(null);
    setDriverEntity(null);
    setDriverMenuDeliveryId(null);
  }, []);

  const selectDriverMenuMemo = useMemo(() => (
    <SelectDriverMenuPresenter
      anchorEl={driverAnchorEl}
      onClose={handleDriverListClose}
      onDriverClick={handleDriverClick}
      driverData={driverEntities as SelectDriverMenuDriverEntity[] || []}
      driverIdsHasDelivery={driverIdsHasDelivery}
      currentDriverId={driverEntity?.id ?? null}
      isTemporaryDriver={driverEntity?.temporary}
    />
  ), [driverAnchorEl, driverEntities, driverEntity?.id, driverEntity?.temporary, driverIdsHasDelivery, handleDriverClick, handleDriverListClose]);

  const resourceLableWithCheckbox = useCallback((arg: ResourceLabelContentArg) => {
    const { resource } = arg;
    const res = resource.extendedProps as unknown as CalendarResourceEntityWithChecked;
    const tooltipTitle = [
      res.overtime ? '勤務時間が超過しています。' : '',
      res.weightOver ? '重量が超過しています。' : '',
      res.volumeOver ? '容量が超過しています。' : '',
      res.outsideSpecifiedTime ? '指定時間が超過しています。' : '',
      res.outsideBerthReservationTime ? 'バース予約の時間外です。' : '',
    ].filter((maybe) => maybe);

    if (res.deliveryId === -1) {
      return (
        <Stack>
          <Button onClick={() => setTemporaryTruckDialogOpen(true)}>
            仮車両追加＋
          </Button>
        </Stack>
      );
    }

    const sameResourcesDeliveryIds = resources.filter((it) => it.id === resource.id).map((it) => it.deliveryId);
    const someFixed = sameResourcesDeliveryIds.some((it) => fixedShiftTruckIds.includes(it));
    const allFixed = sameResourcesDeliveryIds.every((it) => fixedShiftTruckIds.includes(it));

    return (
      <ResourceStackMemo
        resource={resource}
        res={res}
        someFixed={someFixed}
        allFixed={allFixed}
        tooltipTitle={tooltipTitle}
        handleCheckboxChange={handleCheckboxChange}
        handleTruckMenuOpen={handleTruckMenuOpen}
        handleDriverMenuOpen={handleDriverMenuOpen}
        handleTruckLabelClick={handleTruckLabelClick}
      />
    );
  }, [fixedShiftTruckIds, handleCheckboxChange, handleDriverMenuOpen, handleTruckLabelClick, handleTruckMenuOpen, resources]);

  const addTemporaryTruckResource = useMemo(() => (
    {
      id: Number.MAX_SAFE_INTEGER.toString(),
      title: '仮車両追加',
      driverEntity: null,
      truckEntity: null,
      truckName: null,
      driverName: null,
      garageName: null,
      summaries: null,
      workingTime: null,
      resourceIndex: Number.MAX_SAFE_INTEGER,
      hasOperations: false,
      partnerCompanyId: null,
      partnerCompanyName: null,
      deliveryId: -1,
      isChecked: false,
      overtime: false,
      weightOver: false,
      volumeOver: false,
      outsideSpecifiedTime: false,
      outsideBerthReservationTime: false,
      hasWarning: false,
    }
  ), []);

  const resourcesWithCheckedState: CalendarResourceEntityWithChecked[] = useMemo(() => {
    const ret = resources.filter((it) => canDisplay(it)).map((it) => {
      const operations = operationEvents.filter((ev) => ev.resourceId === it.id);

      return {
        ...it,
        isChecked: checkedResources.includes(it.id),
        hasWarning: operations.some((ev) => ev.hasWarning),
        overtime: operations.some((ev) => ev.overtime),
        weightOver: operations.some((ev) => ev.weightOver),
        volumeOver: operations.some((ev) => ev.volumeOver),
        outsideSpecifiedTime: operations.some((ev) => ev.outsideSpecifiedTime),
        outsideBerthReservationTime: operations.some((ev) => ev.outsideBerthReservationTime),
      };
    });
    if (ret.length === 0) {
      return [addTemporaryTruckResource];
    }
    return ret.concat([addTemporaryTruckResource]);
  }, [canDisplay, checkedResources, resources, operationEvents, addTemporaryTruckResource]);

  const handleAllCheckboxChange = useCallback(() => {
    if (selectedState === 'every') {
      setCheckedResources([]);
    } else {
      setCheckedResources(resources.filter((it) => canDisplay(it)).map((it) => it.id));
    }
  }, [resources, selectedState, canDisplay]);

  const selectedDeliveries = useMemo(() => {
    const deliveryIds = deliveryEvents.filter((it) => checkedResources.includes(it.resourceId)).map((it) => it.deliveryId);
    return deliveryEntities.filter((it) => deliveryIds.includes(it.id)).map((it) => {
      const truck = truckData?.find((t) => t.id === it.truckId);
      const driver = driverEntities?.find((d) => d.id === it.driverId);
      const evs = deliveryEvents?.find((d) => d.deliveryId === it.id);
      const continuousGroupRange = it.continuousShiftGroupId
        ? [datetimeDecorator.toYyyyMmDdHhMm(new Date(it.continuousShiftStartAt), '/'), datetimeDecorator.toYyyyMmDdHhMm(new Date(it.continuousShiftEndAt), '/')].join('〜')
        : undefined;
      return {
        id: it.id,
        name: [truckDisplayName(truck), driverDisplayName(driver)].join('／'),
        range: [datetimeDecorator.toYyyyMmDdHhMm(new Date(it.startAt), '/'), datetimeDecorator.toYyyyMmDdHhMm(new Date(it.endAt), '/')].join('〜'),
        count: Array.from(new Set(it.operations.map((o) => o.orderId))).length,
        resourceIndex: evs?.resourceIndex,
        fixed: it.operations.some((op) => op.fixed),
        continuousGroupRange,
      };
    });
  }, [deliveryEvents, deliveryEntities, checkedResources, truckData, driverEntities]);

  useEffect(() => {
    const selectedDeliveryIds = selectedDeliveries.map((it) => it.id);
    plansContext.selectedCycleIndexes.filter((it) => !selectedDeliveryIds.includes(it.deliveryId)).forEach((it) => {
      plansContext.removeCycleIndex(it.deliveryId);
    });
    selectedDeliveryIds.forEach((it) => {
      plansContext.updateCycleIndex({ deliveryId: it, cycleIndexes: [1] });
    });
  }, [selectedDeliveries]);

  useEffect(() => {
    plansContext.setAllDeliverySelected(selectedState === 'every');
  }, [selectedState]);

  useEffect(() => {
    if (context.config?.selected_company_id === 0) {
      setResetTooltip('事業所を選択してください');
    } else {
      setResetTooltip('選択した勤務計画の配送計画をすべて未割り当てにする');
    }
  }, [context.config?.selected_company_id]);

  const resetButtonOnClick = useCallback(() => {
    setResetTargetDeliveryIds(selectedDeliveries.filter((it) => !fixedShiftTruckIds.includes(it.id)).map((d) => d.id));
    setResetDialogOpen(true);
  }, [selectedDeliveries, fixedShiftTruckIds]);

  const handleResetTargetDeliveryCheck = useCallback((deliveryId: number) => {
    setResetTargetDeliveryIds((prev) => (prev.includes(deliveryId)
        ? prev.filter((id) => id !== deliveryId)
        : [...prev, deliveryId]));
  }, []);

  const handleResetDialogOk = useCallback(() => {
    setResetDialogOpen(false);
    const deliveries = deliveryEntities.filter((it) => resetTargetDeliveryIds.includes(it.id));
    const deleteOrderIds = Array.from(new Set(deliveries.flatMap((it) => it.operations.map((o) => o.orderId))));
    if (deleteOrderIds.length > 0) {
      mutateDeleteOrdersOperations(deleteOrderIds);
    } else {
      enqueueSnackbar('削除対象がありませんでした。');
    }
  }, [deliveryEntities, mutateDeleteOrdersOperations, resetTargetDeliveryIds]);

  const handleResetDialogCancel = useCallback(() => {
    setResetDialogOpen(false);
  }, []);

  const resetDialogMemo = useMemo(() => (
    <Dialog open={resetDialogOpen} onClose={handleResetDialogCancel} maxWidth="md" sx={{ maxHeight: '80vh' }}>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          リセット対象の配送計画を選択してください
        </Typography>
        <Stack>
          <Table>
            <TableBody>
              {selectedDeliveries.sort((a, b) => a.range.localeCompare(b.range)).sort((a, b) => (a.resourceIndex - b.resourceIndex)).map((d) => (
                <TableRow key={d.id}>
                  <TableCell>
                    <Checkbox
                      checked={resetTargetDeliveryIds.includes(d.id)}
                      onChange={() => handleResetTargetDeliveryCheck(d.id)}
                      disabled={fixedShiftTruckIds.includes(d.id)}
                      sx={{ p: 0, m: 0, }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography>{d.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{d.range}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{[d.count, '案件'].join('')}</Typography>
                  </TableCell>
                  <TableCell>
                    {fixedShiftTruckIds.includes(d.id) && (<Chip color="info" size="small" label="確定" />)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box flex={1} />
        <Button onClick={handleResetDialogCancel}>キャンセル</Button>
        <Button
          variant="contained"
          onClick={handleResetDialogOk}
          disabled={resetTargetDeliveryIds.length === 0}
          style={{ color: resetTargetDeliveryIds.length === 0 ? '#ccc' : undefined }}
        >
          リセットする
        </Button>
      </DialogActions>
    </Dialog>
  ), [
    resetDialogOpen,
    selectedDeliveries,
    resetTargetDeliveryIds,
    handleResetTargetDeliveryCheck,
    handleResetDialogCancel,
    handleResetDialogOk,
    fixedShiftTruckIds,
  ]);

  const unfixButtonOnClick = useCallback(() => {
    setFixOrUnfix('unfix');
    setFixDialogOpen(true);
  }, []);

  const fixButtonOnClick = useCallback(() => {
    setFixOrUnfix('fix');
    setFixDialogOpen(true);
  }, []);

  const handleFixDialogCancel = useCallback(() => {
    setFixDialogOpen(false);
  }, []);

  const handleFixDialogOk = useCallback(() => {
    if (fixOrUnfix === 'fix') {
      handleConfirmDeliveries(fixedDeliveryIdsForUpdate);
    } else {
      handleUnconfirmDeliveries(fixedDeliveryIdsForUpdate);
    }

    setFixDialogOpen(false);
  }, [fixedDeliveryIdsForUpdate, handleConfirmDeliveries, handleUnconfirmDeliveries, fixOrUnfix]);

  useEffect(() => {
    if (!fixDialogOpen) return;

    const checkedDeliveries = selectedDeliveries.map((it) => it.id);
    setFixedDeliveryIdsForUpdate(checkedDeliveries);
  }, [fixDialogOpen, fixedShiftTruckIds, selectedDeliveries]);

  const fixDialogMemo = useMemo(() => (
    <Dialog open={fixDialogOpen} onClose={handleFixDialogCancel} maxWidth="md" sx={{ maxHeight: '80vh' }}>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          {fixOrUnfix === 'fix'
            ? '確定対象の配送計画を選択してください'
            : '解除対象の配送計画を選択してください'}
        </Typography>
        <Stack>
          <Table>
            <TableBody>
              {selectedDeliveries.sort((a, b) => a.range.localeCompare(b.range)).sort((a, b) => (a.resourceIndex - b.resourceIndex)).map((d, idx) => (
                <React.Fragment key={[d.id, idx].join('_')}>
                  <TableRow key={[d.id, idx].join('_')}>
                    <TableCell
                      rowSpan={d.continuousGroupRange ? 2 : 1}
                    >
                      <Checkbox
                        checked={fixedDeliveryIdsForUpdate.includes(d.id)}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setFixedDeliveryIdsForUpdate((prev) => {
                            if (event.target.checked) {
                              return [...prev, d.id];
                            }
                            return prev.filter((it) => it !== d.id);
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell
                      rowSpan={d.continuousGroupRange ? 2 : 1}
                    >
                      {fixedShiftTruckIds.includes(d.id) && (<Chip color="info" size="small" label="確定" />)}
                    </TableCell>
                    <TableCell>
                      <Typography>{d.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{d.range}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{[d.count, '案件'].join('')}</Typography>
                    </TableCell>
                  </TableRow>
                  {d.continuousGroupRange && (
                    <TableRow key={[d.id, 'ext'].join('_')}>
                      <TableCell colSpan={3} sx={{ p: 0, m: 0, pb: 1, }}>
                        <Typography>
                          {`帰庫しない連続勤務のため、${d.continuousGroupRange}の勤務すべてを${fixOrUnfix === 'fix' ? '確定' : '解除'}します。`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box flex={1} />
        <Button onClick={handleFixDialogCancel}>キャンセル</Button>
        {fixOrUnfix === 'fix'
          ? (<Button variant="contained" onClick={handleFixDialogOk}>確定する</Button>)
          : (<Button variant="contained" onClick={handleFixDialogOk}>解除する</Button>)}
      </DialogActions>
    </Dialog>
  ), [fixDialogOpen, handleFixDialogCancel, selectedDeliveries, handleFixDialogOk, fixedDeliveryIdsForUpdate, fixedShiftTruckIds, fixOrUnfix]);

  const ResourceAreaColumns = useMemo(() => ([
    {
      headerContent: (
        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          sx={{
            justifyContent: 'space-between',
            minWidth: '100%'
          }}
        >
          <Stack direction="row" alignItems="center" sx={{ flexGrow: 1 }}>
            <Badge
              color="primary"
              badgeContent={checkedResources.length}
              max={10000}
              sx={{
                '& .MuiBadge-badge': {
                  top: '3px', // バッジを下に移動
                  right: '1px'
                }
              }}
            >
              <Checkbox
                checked={selectedState === 'every'}
                indeterminate={selectedState === 'some'}
                onChange={handleAllCheckboxChange}
              />
            </Badge>
            {(checkedResources.length > 0) && (
              <Tooltip
                title={resetTooltip}
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={isLoading || !!currentHistoryVersion || (context.config?.selected_company_id === 0)}
                    onClick={resetButtonOnClick}
                  >
                    <RotateLeftRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <TruckSelectMenuPresenter
              truckDisplayStatus={truckDisplayStatus}
              groupEntities={groupEntities}
            />
          </Stack>
          {(checkedResources.length > 0) && (
            <Stack
              direction="row"
              gap={0.5}
              sx={{
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={unfixButtonOnClick}
                disabled={isLoading || !!currentHistoryVersion || (context.config?.selected_company_id === 0)}
              >
                確定解除
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={fixButtonOnClick}
                disabled={isLoading || !!currentHistoryVersion || (context.config?.selected_company_id === 0)}
              >
                配車確定
              </Button>
            </Stack>
          )}
        </Stack>
      ),
      field: 'title',
      // width: '400px',
      headerClassNames: 'custom-resource-label', // ヘッダーのクラス
    },
  ]), [selectedState, handleAllCheckboxChange, checkedResources.length, resetTooltip, isLoading, currentHistoryVersion, context.config, resetButtonOnClick, truckDisplayStatus, groupEntities, fixButtonOnClick]);

  const toggleDrawer = useCallback(() => {
    setOpenUnallocatedOrders(!openUnallocatedOrders);
  }, [openUnallocatedOrders]);

  const backDropMemo = useMemo(() => (
    <Backdrop
      sx={{ color: '#fff', zIndex: 1000 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ), [isLoading]);

  const resetAllocateHistories = useCallback(() => {
    // void
  }, []);

  const planningButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.TIMELINE, button_name: '自動配車' });
    const targets = [
      checkedResources.length === 0 ? '車両' : '',
    ].filter((it) => it);
    if (targets.length > 0) {
      closeSnackbar();
      const message = `配車する${targets.join('・')}を選択してください。`;
      ReactGA.event('error', { screen_name: SCREEN_NAMES.TIMELINE, button_name: '自動配車', label: message });
      enqueueSnackbar(message);
      return;
    }
    setPlanningDialogIsOpen(true);
  }, [closeSnackbar, enqueueSnackbar, checkedResources]);

  const planningDialogOnClose = useCallback(() => {
    setPlanningDialogIsOpen(false);
  }, []);

  const selectedDeliveryIds = useMemo(() => (
    arrayUtil.uniq(resources.filter((it) => checkedResources.includes(it.id)).map((it) => it.deliveryId))
  ), [checkedResources, resources]);

  const planningMemo = useMemo(() => (
    <Dialog
      open={planningDialogIsOpen}
      onClose={planningDialogOnClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: { maxWidth: 720 } }}
    >
      <PlanningPresenter
        screenName={SCREEN_NAMES.TIMELINE}
        planningRequest={planningRequest}
        updateBalancedLoading={updateBalancedLoading}
        updateExcludeToll={updateExcludeToll}
        onClose={planningDialogOnClose}
        selectedDeliveryIds={selectedDeliveryIds}
        selectedOrderIds={selectedOrderIds}
        startOn={startOn}
        updateIncludeFutureDeliveries={updateIncludeFutureDeliveries}
        updateAutoSplitOrders={updateAutoSplitOrders}
        updateDontExchangePreroute={updateDontExchangePreroute}
        updateConcurrentAllOrNothing={updateConcurrentAllOrNothing}
        relaxedRules={relaxedRules}
        selectedRelaxedRules={selectedRelaxedRules}
        setSelectedRelaxedRules={setSelectedRelaxedRules}
        setPriorityLargeTrucks={setPriorityLargeTrucks}
        priorityLargeTrucks={priorityLargeTrucks}
        updateExpandWorkingStartMinutes={updateExpandWorkingStartMinutes}
        updateExpandWorkingEndMinutes={updateExpandWorkingEndMinutes}
        updateExpandLoadStartMinutes={updateExpandLoadStartMinutes}
        updateExpandLoadEndMinutes={updateExpandLoadEndMinutes}
        updateExpandUnloadStartMinutes={updateExpandUnloadStartMinutes}
        updateExpandUnloadEndMinutes={updateExpandUnloadEndMinutes}
        updateExpandMaxVolumeRate={updateExpandMaxVolumeRate}
        updateExpandMaxLoadCapacityRate={updateExpandMaxLoadCapacityRate}
        updateActivateIncrementalMaxNumPistons={updateActivateIncrementalMaxNumPistons}
        updateSenario={updateSenario}
        updateDisableGlobalOpt={updateDisableGlobalOpt}
        updateTraceCompanyId={updateTraceCompanyId}
      />
    </Dialog>
  ), [planningDialogIsOpen, planningDialogOnClose, planningRequest, priorityLargeTrucks, relaxedRules, selectedDeliveryIds, selectedOrderIds, selectedRelaxedRules, setPriorityLargeTrucks, setSelectedRelaxedRules, startOn, updateAutoSplitOrders, updateBalancedLoading, updateConcurrentAllOrNothing, updateDontExchangePreroute, updateExcludeToll, updateExpandLoadEndMinutes, updateExpandLoadStartMinutes, updateExpandMaxLoadCapacityRate, updateExpandMaxVolumeRate, updateExpandUnloadEndMinutes, updateExpandUnloadStartMinutes, updateExpandWorkingEndMinutes, updateExpandWorkingStartMinutes, updateIncludeFutureDeliveries, updateActivateIncrementalMaxNumPistons, updateSenario, updateDisableGlobalOpt, updateTraceCompanyId]);

  const importButtonOnClick = useCallback(() => {
    void navigate('/import');
  }, []);

  const estimationMethodOnClick = useCallback((value: EstimationMethodVo) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.TIMELINE, button_name: `見積もり - ${value}` });
    setEstimationMethod(value);
  }, []);

  const estimationButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.TIMELINE, button_name: '見積もり' });
    setEstimationDialogIsOpen(true);
  }, []);
  const estimationOnClose = useCallback(() => {
    ReactGA.event('close', { screen_name: SCREEN_NAMES.TIMELINE, button_name: '見積もり' });
    setEstimationDialogIsOpen(false);
    setEstimationMethod(undefined);
  }, []);

  const allocateHistoriesOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.TIMELINE, button_name: '元に戻す' });
    setAllocatedHistoriesDialogIsOpen(true);
    setOpenUnallocatedOrders(true);
  }, []);

  const allocateHistoryCancelButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.TIMELINE, button_name: '元に戻す キャンセル' });
    resetCurrentHistoryVersion();
    setAllocatedHistoriesDialogIsOpen(false);
  }, [resetCurrentHistoryVersion]);

  const allocateHistoryButtonOnClick = useCallback(() => {
    ReactGA.event('apply', { screen_name: SCREEN_NAMES.TIMELINE, button_name: '元に戻す' });
    rollbackRequest();
  }, [rollbackRequest]);

  useEffect(() => {
    const list = plansContext.allocateHistories.filter((it) => it.companyId === context.config?.selected_company_id);
    setAllocateHistoriesForCompany(list);
  }, [plansContext.allocateHistories, context.config?.selected_company_id]);

  useEffect(() => {
    setAllocatedHistoriesDialogIsOpen(!!currentHistoryVersion);
  }, [currentHistoryVersion]);

  const scenarioDialogMemo = useMemo(() => (
    <ScenarioPlanningListDialog
      dialogIsOpen={scenarioPlanningDialogIsOpen}
      setDialogIsOpen={setScenarioPlanningDialogIsOpen}
      customInputFields={customInputFields}
      requestScenarioPlanning={requestScenarioPlanning}
    />
  ), [customInputFields, scenarioPlanningDialogIsOpen, requestScenarioPlanning]);

  const onTariffCalculationExecute = useCallback((params: TariffCalculationParams) => {
    handleTariffCalculationExecute(params, () => setTariffCalculationDialogIsOpen(false));
  }, [handleTariffCalculationExecute]);

  const tariffCalculationDialogMemo = useMemo(() => (
    <TariffCalculationDialog
      dialogIsOpen={tariffCalculationDialogIsOpen}
      setDialogIsOpen={setTariffCalculationDialogIsOpen}
      selectedDeliveryIds={selectedDeliveryIds}
      onExecute={onTariffCalculationExecute}
      isExecuting={tariffCalculationIsExecuting}
    />
  ), [tariffCalculationDialogIsOpen, selectedDeliveryIds, onTariffCalculationExecute, tariffCalculationIsExecuting]);

  const actionsMemo = useMemo(() => (
    <ActionsPresenter
      startOn={startOn}
      endOn={endOn}
      isLoading={isLoading}
      planningButtonOnClick={planningButtonOnClick}
      selectImportMethodButtonOnClick={importButtonOnClick}
      deliveryIdSelected={checkedResources.length > 0}
      estimationButtonOnClick={estimationButtonOnClick}
      allocateHistoriesOnClick={allocateHistoriesOnClick}
      allocateHistoriesForCompany={allocateHistoriesForCompany}
      currentHistoryVersion={currentHistoryVersion}
      resetAllocateHistories={resetAllocateHistories}
      setScenarioPlanningDialogIsOpen={setScenarioPlanningDialogIsOpen}
      currentView="timeline"
      updateView={undefined} // {updateView}
      screenName={SCREEN_NAMES.TIMELINE}
    />
  ), [startOn, endOn, isLoading, planningButtonOnClick, importButtonOnClick, checkedResources.length, estimationButtonOnClick, allocateHistoriesOnClick, allocateHistoriesForCompany, currentHistoryVersion, resetAllocateHistories]);

  const customEventContent = (arg: EventContentArg) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const type = arg.event.extendedProps.displayType;

    switch (type) {
      case 'drive':
        return (
          <div
            style={{
              height: '3px',
              backgroundColor: 'blue',
              maxWidth: '100%',
              marginTop: '9px',
              marginLeft: 0,
              marginRight: 0,
              padding: 0,
            }}
          />
        );
      case 'rest':
        return (
          <div
            style={{
              height: '3px',
              borderBottom: '2px dotted gray',
              maxWidth: '100%',
              marginTop: '8px',
              marginLeft: 0,
              marginRight: 0,
              padding: 0,
            }}
          />
        );
      case 'null':
        return (
          <div
            style={{
              height: '3px',
              borderBottom: '2px dashed gray',
                maxWidth: '100%',
              marginTop: '8px',
              marginLeft: 0,
              marginRight: 0,
              padding: 0,
            }}
          />
        );
      default:
        return {
          html: `<div class="fc-event-title fc-sticky">${arg.event.title}</div>`
        };
    }
  };

  useEffect(() => {
    // カレンダーの開始日を動かす処理。これがないと開始日が固定のまま動かない。
    if (calendarRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      calendarRef.current.getApi().gotoDate(startDate);
    }
  }, [startDate]);

  const handleCostDetailClick = useCallback(() => {
    setCostAnalysisDialogOpen(true);
  }, []);

  // TODO: コスト計算機能の復活対応
  useEffect(() => {
    if (![deliveryEntities, truckData, driverEntities, context.config].every((maybe) => maybe)) return;

    const deliveryDetails: { deliveryId: number, details: DetailItem[]}[] = [];

    const arr: DriverDailyCost[] = [];
    deliveryEntities.sort((a, b) => {
      let ret = a.driverId - b.driverId;
      if (ret === 0) {
        ret = new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
      }
      return ret;
    }).forEach((delivery) => {
      if (delivery.operations.length === 0) return;

      const details: DetailItem[] = (delivery.departureOperation?.detail || []).concat(delivery.operations.flatMap((op) => op.detail)).concat(delivery.arrivalOperation?.detail || []);
      if (details.length === 0) {
        deliveryDetails.push({ deliveryId: delivery.id, details: null });
      } else {
        deliveryDetails.push({ deliveryId: delivery.id, details });
      }
    });

    deliveryEntities.forEach(((delivery) => {
      if (delivery.operations.length === 0 && !delivery.workingSeconds) return; // 配送計画に運行がなく、労働時間も設定がなければスキップ

      const driver = driverEntities?.find((d) => d.id === delivery.driverId);
      if (!driver) return;
      const truck = truckData?.find((t) => t.id === delivery.truckId);
      if (!truck) return;

      const dayStartAt: Date = new Date(delivery.workStartAt || delivery.startAt);
      const dayEndAt: Date = new Date(delivery.workEndAt || delivery.endAt);

      let { workingSeconds } = delivery;
      if (!workingSeconds) {
        workingSeconds = (dayEndAt.getTime() - dayStartAt.getTime()) / 1000; // ミリ秒を秒に変換
      }
      if (workingSeconds < 0) return; // 勤務時間がマイナスの場合はスキップ
      const workingHours = workingSeconds / 3600; // 秒を時間に変換
      let cost = 0;
      const dailyCostPerHours = driver.driverCostYenPerHours || context.config.driver_cost_yen_per_hours;
      if ([workingHours, dailyCostPerHours].every((it) => it)) {
        cost = workingHours * dailyCostPerHours;
      }
      const drivingMm = delivery.operations.reduce((acc, op) => acc + op.drivingDistanceMm, 0);
      if (drivingMm > 0 && (truck.truckFuelCostYenPerKm || context.config.truck_fuel_cost_yen_per_km)) {
        const fuelCost = (drivingMm / 1000 / 1000) * (truck.truckFuelCostYenPerKm + context.config.truck_fuel_cost_yen_per_km);
        cost += fuelCost;
      }
      [truck.truckInsuranceFeeYenPerDay || context.config.truck_insurance_fee_yen_per_day || 0,
        truck.truckRepairCostYenPerDay || context.config.truck_repair_cost_yen_per_day || 0,
        truck.truckExpresswayFeeYenPerShift || context.config.truck_expressway_fee_yen_per_shift || 0
      ].forEach((fee) => {
        if (fee) cost += fee;
      });
      cost = Math.ceil(cost); // 小数点以下は切り上げ

      const deliveryOrderIds = Array.from(new Set(delivery.operations.filter((it) => it.action === '降').map((op) => op.orderId)));
      const deliveryOrders = deliveryOrderIds.map((it) => shipmentOrderLegRunEntityMap.get(it));
      const billingAmountIds: number[] = [];
      const billingAmounts = deliveryOrders.map((order) => order?.shipment_order_billing_amount).filter((it) => it).filter((it) => {
        if (billingAmountIds.includes(it.id)) return false;
        billingAmountIds.push(it.id);
        return true;
      });
      const sales = billingAmounts.reduce((acc, bill) => (acc + (bill.total_amount || 0)), 0);
      const currentDate = datetimeDecorator.toYyyyMmDd(new Date(delivery.startAt), '/');
      if (datetimeDecorator.toYyyyMmDd(startDate, '/') <= currentDate && currentDate <= datetimeDecorator.toYyyyMmDd(endDate, '/')) {
        arr.push({
          deliveryId: delivery.id,
          driverId: driver.id,
          truckId: truck.id,
          truckName: truck.temporary ? TRUCK_UNSET_LABEL : truck.licensePlateValue,
          truckKlass: truck.klass || '車両タイプなし',
          date: datetimeDecorator.toYyyyMmDd(new Date(delivery.startAt), '/'),
          workingHours,
          dayStartAt,
          dayEndAt,
          cost,
          sales
        });
      }
    }));

    setDriverDailyCosts(arr);
  }, [context.config, deliveryEntities, driverEntities, endDate, shipmentOrderLegRunEntityMap, startDate, truckData]);

  const handleExpandCostClick = useCallback(() => {
    if (summaryElementCount === 3) {
      setSummaryElementCount(10000);
    } else {
      setSummaryElementCount(3);
    }
  }, [summaryElementCount]);

  useEffect(() => {
    const salesElement = document.querySelector('.sales-info');
    if (!salesElement) return;

    let html = '';
    const costMap = driverDailyCosts.reduce((acc, cost) => {
      let tmp = acc.get(cost.date);
      if (tmp) {
        tmp.cost += cost.cost;
        tmp.sales += cost.sales;
      } else {
        tmp = { sales: cost.sales, cost: cost.cost };
      }
      acc.set(cost.date, tmp);
      return acc;
    }, new Map<string, { sales: number, cost: number }>());

    let lineCount = 0;
    Array.from(costMap.keys()).sort((a, b) => a.localeCompare(b)).forEach((key, index) => {
      const value = costMap.get(key);
      lineCount++;
      if (index >= summaryElementCount) return;

      const sales = Math.ceil(value.sales);
      const cost = Math.ceil(value.cost);
      const profit = sales - cost;
      html += `<tr class="sales-row">
        <td class="sales-date">${key}</td>
        <td class="sales-items">|</td>
        <td class="sales-items">売上</td>
        <td class="sales-details">¥${sales.toLocaleString()}</td>
        <td class="sales-items">|</td>
        <td class="sales-items">原価</td>
        <td class="sales-details">¥${cost.toLocaleString()}</td>
        <td class="sales-items">|</td>
        <td class="sales-items">利益</td>
        <td class="sales-details">¥${profit.toLocaleString()}</td>
      </tr>`;
    });

    let expandHtml = '';
    if (lineCount > 3) {
      if (summaryElementCount === 3) {
        expandHtml = '<span class="expand-sales-cost">広げる</span>';
      } else {
        expandHtml = '<span class="expand-sales-cost">閉じる</span>';
      }
    }
    const detail = `<div><span class="sales-cost-detail">詳細</span>${expandHtml}</div>`;

    if (html !== '') {
      html = `<table class="sales-table"><tr>${html}</tr></table>${detail}`;
    }

    salesElement.innerHTML = html;
    setSummaryElementHeight(Math.max(salesElement.scrollHeight - 50, -10));

    // クリックイベントリスナーを追加
    const detailElement = salesElement.querySelector('.sales-cost-detail');
    if (detailElement) {
      detailElement.addEventListener('click', handleCostDetailClick);
    }
    const expandElement = salesElement.querySelector('.expand-sales-cost');
    if (expandElement) {
      expandElement.addEventListener('click', handleExpandCostClick);
    }

    // クリーンアップ関数でイベントリスナーを削除
    return () => {
      const currentDetailElement = salesElement.querySelector('.sales-cost-detail');
      if (currentDetailElement) {
        currentDetailElement.removeEventListener('click', handleCostDetailClick);
      }
    };
  }, [driverDailyCosts, endDate, startDate, summaryElementCount]);

  // カレンダーレンダリング後にヘッダーを修正するuseEffect
  useEffect(() => {
    function addSalesInfo() {
      const headers = document.querySelectorAll('.fc-toolbar-title');
      const salesInfo = document.querySelector('.sales-info');
      if (salesInfo) {
        return;
      }

      const salesElement = document.createElement('div');
      salesElement.className = 'sales-info';

      if (headers[0]) {
        headers[0].appendChild(salesElement);
      }
    }

    // 初回実行
    addSalesInfo();

    // ビュー変更時やその他の更新時に実行するためのMutationObserver
    const observer = new MutationObserver(addSalesInfo);
    const container = document.querySelector('.fc');
    if (container) {
      observer.observe(container, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const calendarMemo = useMemo(() => (
    <Stack
      px={1}
    >
      <FullCalendar
        ref={calendarRef}
        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
        now={startDate}
        plugins={[resourceTimelinePlugin, interactionPlugin]}
        headerToolbar={HeaderToolbar}
        views={Views}
        initialView={initialView}
        datesSet={(dataInfo) => {
          handleViewChange(dataInfo.view.type);
        }}
        duration={calendarDuration}
        locale="ja"
        contentHeight={`calc(100vh - ${appBarHeight + 130 + summaryElementHeight}px)`}
        events={events}
        eventContent={customEventContent}
        eventClassNames={(arg) => {
          const { hasWarning } = arg.event.extendedProps;
          return hasWarning ? ['has-warning-event'] : [];
        }}
        resourceGroupField="partnerCompanyName"
        resources={resourcesWithCheckedState}
        resourceOrder="-resourceOrder,resourceIndex"
        resourceLabelContent={resourceLableWithCheckbox}
        resourceLabelClassNames="custom-resource-label"
        selectable
        eventChange={onChange}
        eventClick={onClickEvent}
        editable
        droppable
        drop={onDrop}
        eventMouseEnter={handlePopoverOpen}
        eventMouseLeave={handlePopoverClose}
        eventDragStart={handlePopoverClose}
        resourceAreaColumns={ResourceAreaColumns}
        scrollTime={getSlotMinTime()}
      />
    </Stack>
  ), [startDate, HeaderToolbar, Views, initialView, calendarDuration, events, resourcesWithCheckedState, resourceLableWithCheckbox, onChange, onClickEvent, onDrop, handlePopoverOpen, handlePopoverClose, ResourceAreaColumns, getSlotMinTime, handleViewChange, summaryElementHeight]);

  const popoverMemo = useMemo(() => {
    if (popoverOperationEventEntity === null) return null;

    let text = '';
    if (popoverOperationEventEntity.title) {
      text += popoverOperationEventEntity.title;
    }
    if (popoverOperationEventEntity?.start) {
      text += ` ${datetimeDecorator.toHourMinutes(new Date(popoverOperationEventEntity.start))}`;
    }
    if (popoverOperationEventEntity?.end) {
      text += ` - ${datetimeDecorator.toHourMinutes(new Date(popoverOperationEventEntity.end))}`;
    }
    if (text === '') {
      return null;
    }

    return (
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={Boolean(popoverEl) && mousePosition !== null}
        anchorReference="anchorPosition"
        anchorPosition={mousePosition ? {
          top: mousePosition.y,
          left: mousePosition.x
        } : undefined}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        hideBackdrop
        disableScrollLock
      >
        <Typography sx={{ p: 1 }}>
          {text}
        </Typography>
      </Popover>
    );
  }, [handlePopoverClose, mousePosition, popoverEl, popoverOperationEventEntity]);

  const ordersMemo = useMemo(() => (
    <OrdersPresenter
      ids={orderIds}
      selectedIds={selectedOrderIds}
      addSelectedId={addSelectedOrderId}
      removeSelectedId={removeSelectedOrderId}
      unit={unit}
      isLoading={isLoading}
      startOn={format(startDate, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' })}
      endOn={format(endDate, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' })}
      mutateDeleteOrder={mutateDeleteOrder}
      mutateDeleteSpecificOrder={mutateDeleteSpecificOrder}
      mutateCloneOrder={mutateCloneOrder}
      planningOrderStatisticsEntity={planningOrderStatisticsEntity}
      resetAllocateHistories={resetAllocateHistories}
      customInputFields={customInputFields}
      truckEntities={truckData}
      driverEntities={driverEntities}
      deliveryEntities={deliveryEntities}
      shipmentOrderLegRunEntityMap={shipmentOrderLegRunEntityMap}
      unallocatedDrawerWidth={unallocatedDrawerWidth}
      mutateRestoreSplittedOrder={mutateRestoreSplittedOrder}
      mutateRestoreSplittedOrders={mutateRestoreSplittedOrders}
      showLegRunLoading={showLegRunLoading}
    />
  ), [orderIds, selectedOrderIds, addSelectedOrderId, removeSelectedOrderId, unit, isLoading, startDate, endDate, mutateDeleteOrder, mutateDeleteSpecificOrder, mutateCloneOrder, planningOrderStatisticsEntity, resetAllocateHistories, customInputFields, truckData, driverEntities, deliveryEntities, shipmentOrderLegRunEntityMap, unallocatedDrawerWidth, mutateRestoreSplittedOrder, mutateRestoreSplittedOrders, showLegRunLoading]);

  useEffect(() => {
    const draggableEl = document.getElementById(draggableElementId);
    if (!draggableEl) return;

    const draggableInstance = new Draggable(draggableEl, {
      itemSelector: `.${draggableElementClassName}`,
    });

    // クリーンアップ関数でインスタンスを明示的に破棄
    return () => {
      draggableInstance.destroy();
    };
  }, [ordersMemo]);

  const allocateHistoryListItemButtonOnClick = useCallback((version: string) => {
    setCurrentHistoryVersion(version);
  }, [setCurrentHistoryVersion]);

  const allocateHistoriesMemo = useMemo(() => (
    <AllocateHistoryPresenter
      allocateHistoriesForCompany={allocateHistoriesForCompany}
      allocateHistoryListItemButtonOnClick={allocateHistoryListItemButtonOnClick}
      allocateHistoryButtonOnClick={allocateHistoryButtonOnClick}
      allocateHistoryCancelButtonOnClick={allocateHistoryCancelButtonOnClick}
      isLoading={isLoading}
      currentHistoryVersion={currentHistoryVersion}
      unallocatedDrawerWidth={unallocatedDrawerWidth}
    />
  ), [allocateHistoriesForCompany, allocateHistoryButtonOnClick, allocateHistoryCancelButtonOnClick, allocateHistoryListItemButtonOnClick, currentHistoryVersion, isLoading, unallocatedDrawerWidth]);

  const unallocatedOrdersMemo = useMemo(() => (
    <Stack>
      <IconButton
        sx={{
          position: 'fixed',
          right: openUnallocatedOrders ? `${unallocatedDrawerWidth - 5}px` : '-5px',
          bottom: '5%',
          zIndex: 998,
          backgroundColor: theme.palette.background.paper,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: theme.colors.secondary.light,
        }}
        onClick={() => toggleDrawer()}
      >
        {openUnallocatedOrders
        ? <KeyboardArrowRightIcon />
        : <KeyboardArrowLeftIcon />}
      </IconButton>
      <Box
        sx={{
          width: openUnallocatedOrders ? unallocatedDrawerWidth : 0,
          bgcolor: theme.colors.alpha.white[100],
          position: 'absolute',
          right: 0,
          top: actionBarHeight,
          zIndex: 999,
          overflow: 'hidden',
          height: `calc(100vh - ${appBarHeight + actionBarHeight}px)`,
        }}
      >
        {allocatedHistoriesDialogIsOpen ? allocateHistoriesMemo : ordersMemo}
      </Box>
    </Stack>
  ), [openUnallocatedOrders, unallocatedDrawerWidth, theme.palette.background.paper, theme.colors.secondary.light, theme.colors.alpha.white, ordersMemo, toggleDrawer, allocatedHistoriesDialogIsOpen, allocateHistoriesMemo]);

  const confirmDialogMemo = useMemo(() => (
    <ConfirmDialog
      open={confirmDialogIsOpen}
      message={confirmDialogMessage}
      handleOk={confirmDialogHandleOk}
      handleCancel={confirmDialogHandleCancel}
    />
  ), [confirmDialogHandleCancel, confirmDialogHandleOk, confirmDialogIsOpen, confirmDialogMessage]);

  const handleSuggestionDialogClose = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setSuggestionDialogOpen(false);
  }, []);

  return (
    <>
      <title>配車計画 | タイムライン</title>
      <Paper>
        <Stack>
          {planningMemo}
          {scenarioDialogMemo}
          {tariffCalculationDialogMemo}
          <Stack
            sx={{
              height: actionBarHeight,
              bgcolor: theme.colors.alpha.white[100],
            }}
          >
            {actionsMemo}
          </Stack>
          <Stack
            sx={{
              marginRight: openUnallocatedOrders ? `${unallocatedDrawerWidth}px` : 0,
              minWidth: theme.breakpoints.values.md,
              position: 'relative',
            }}
          >
            {/* 計算中スピナーを追加 */}
            {calculating && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '20px',
                  top: '25px',
                  transform: 'translateY(-50%)',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
            {calendarMemo}
            {popoverMemo}
          </Stack>
          {unallocatedOrdersMemo}
        </Stack>

        {truckInfoPanel}
        {confirmDialogMemo}
        {planningOrderPresenterMemo}
        {notAllocSuggestions && (
          <NotAllocSuggestionComponent
            showNotAllocSuggestionSnackbar={showNotAllocSuggestionSnackbar}
            suggestionDialogOpen={suggestionDialogOpen}
            notAllocSuggestions={notAllocSuggestions}
            handleSuggestionDialogClose={handleSuggestionDialogClose}
            setShowNotAllocSuggestionSnackbar={setShowNotAllocSuggestionSnackbar}
            setSuggestionDialogOpen={setSuggestionDialogOpen}
          />
        )}
        <DirectionSettingDialog
          open={directionSettingDialogIsOpen}
          onClose={() => {
            updateDirectionSettingDialogIsOpen(false);
          }}
          width="md"
          navigateToPath={printOperationDirectionUrl()}
        />
        {backDropMemo}
        {resetDialogMemo}
        {fixDialogMemo}
        <OperationInfoPanel
          open={Boolean(anchorEl)}
          operation={selectedOperation}
          deliveryData={deliveryEntities}
          truckData={truckData}
          driverEntities={driverEntities}
          onClose={() => setAnchorEl(null)}
          anchorEl={anchorEl}
          eventDialogSelectedOrderIds={eventDialogSelectedOrderIds}
          addEventDialogSelectedOrderIds={addEventDialogSelectedOrderIds}
          deleteButtonOnClick={deleteButtonOnClick}
          fixedShiftTruckIds={fixedShiftTruckIds}
        />
        <EstimationDialogPresenter
          estimationDialogIsOpen={estimationDialogIsOpen}
          estimationOnClose={estimationOnClose}
          estimationMethod={estimationMethod}
          estimationMethodOnClick={estimationMethodOnClick}
          deliveryEntities={deliveryEntities}
          driverEntities={driverEntities}
          truckEntities={truckData}
          selectedDeliveryIds={selectedDeliveryIds}
        />
        {(startOn && endOn && (
          <PlanningTemporaryTruckDialog
            startOn={startOn}
            endOn={endOn}
            temporaryTruckDialogOpen={temporaryTruckDialogOpen}
            setTemporaryTruckDialogOpen={setTemporaryTruckDialogOpen}
          />
        ))}
        {selectTruckMenuMemo}
        {selectDriverMenuMemo}
        <CostAnalysisDialog
          open={costAnalysisDialogOpen}
          onClose={() => setCostAnalysisDialogOpen(false)}
          driverDailyCosts={driverDailyCosts}
          startDate={startDate}
          endDate={endDate}
        />
      </Paper>
    </>
  );
});

export default Presenter;
